import { useState, useRef, useEffect, useCallback } from "react";
import { auth, provider } from "./firebase";
import { signInWithPopup, signOut, onAuthStateChanged } from "firebase/auth";

const API_URL   = process.env.REACT_APP_API_URL   || "http://127.0.0.1:8000/generate";
const AGENT_URL = process.env.REACT_APP_AGENT_URL || "https://smartcart-agent-505176174078.us-central1.run.app/chat";
const BASE_URL  = (process.env.REACT_APP_API_URL  || "http://127.0.0.1:8000/generate").replace("/generate", "");

const T = {
  bg:"#F7F5F0", surface:"#FFFFFF", surfaceAlt:"#F2EFE9",
  border:"#E0DDD6", borderDark:"#C8C4BC",
  ink:"#1A1814", inkSec:"#6B6760", inkTer:"#9E9B96",
  green:"#2D6A4F", greenLight:"#D8EDDF", greenMid:"#52B788",
  amber:"#B5600A", amberLight:"#FDEFD3",
  red:"#C0392B", redLight:"#FDECEA",
  blue:"#1B4F8A", blueLight:"#DDEAF8",
};

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;1,400&family=DM+Mono:wght@400;500&family=Lato:wght@300;400;700&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body { background: ${T.bg}; color: ${T.ink}; font-family: 'Futura', 'Trebuchet MS', sans-serif; line-height: 1.6; }
  ::-webkit-scrollbar { width: 5px; }
  ::-webkit-scrollbar-track { background: ${T.bg}; }
  ::-webkit-scrollbar-thumb { background: ${T.borderDark}; border-radius: 3px; }
  @keyframes fadeUp { from { opacity:0; transform:translateY(20px); } to { opacity:1; transform:translateY(0); } }
  @keyframes spin { to { transform: rotate(360deg); } }
  @keyframes slideIn { from { opacity:0; transform:translateX(20px); } to { opacity:1; transform:translateX(0); } }
  @keyframes slideInLeft { from { opacity:0; transform:translateX(-20px); } to { opacity:1; transform:translateX(0); } }
  @keyframes pulse { 0%,100% { opacity:1; } 50% { opacity:0.4; } }
  @keyframes micPulse { 0%,100% { box-shadow: 0 0 0 0 rgba(192,57,43,0.4); } 70% { box-shadow: 0 0 0 10px rgba(192,57,43,0); } }
  .fu  { animation: fadeUp 0.5s ease both; }
  .fu1 { animation: fadeUp 0.5s 0.05s ease both; }
  .fu2 { animation: fadeUp 0.5s 0.10s ease both; }
  .fu3 { animation: fadeUp 0.5s 0.15s ease both; }
  .chat-panel { animation: slideIn 0.25s ease both; }
  .nav-panel  { animation: slideInLeft 0.25s ease both; }
  .chat-msg-user { animation: fadeUp 0.2s ease both; }
  .chat-msg-agent { animation: fadeUp 0.2s ease both; }
  .mic-active { animation: micPulse 1.2s ease infinite; }
`;

// ── Shared UI ────────────────────────────────────────────────────────────────

function Label({ children }) {
  return <span style={{ display:"block", fontSize:11, fontWeight:700, letterSpacing:"0.06em", textTransform:"uppercase", color:T.inkSec, marginBottom:5 }}>{children}</span>;
}

function Input({ label, value, onChange, placeholder, type="text" }) {
  const [f, setF] = useState(false);
  return (
    <label style={{ display:"flex", flexDirection:"column", gap:4 }}>
      {label && <Label>{label}</Label>}
      <input type={type} value={value} onChange={onChange} placeholder={placeholder}
        onFocus={()=>setF(true)} onBlur={()=>setF(false)}
        style={{ background:T.surfaceAlt, border:`1px solid ${f?T.ink:T.border}`, borderRadius:4, padding:"10px 12px", fontSize:14, color:T.ink, fontFamily:"'Lato',sans-serif", outline:"none", width:"100%", transition:"border-color 0.15s" }} />
    </label>
  );
}

function Textarea({ label, value, onChange, placeholder, rows=3 }) {
  const [f, setF] = useState(false);
  return (
    <label style={{ display:"flex", flexDirection:"column", gap:4 }}>
      {label && <Label>{label}</Label>}
      <textarea value={value} onChange={onChange} placeholder={placeholder} rows={rows}
        onFocus={()=>setF(true)} onBlur={()=>setF(false)}
        style={{ background:T.surfaceAlt, border:`1px solid ${f?T.ink:T.border}`, borderRadius:4, padding:"10px 12px", fontSize:14, color:T.ink, fontFamily:"'Lato',sans-serif", outline:"none", width:"100%", resize:"vertical", transition:"border-color 0.15s" }} />
    </label>
  );
}

function Select({ label, value, onChange, options }) {
  return (
    <label style={{ display:"flex", flexDirection:"column", gap:4 }}>
      {label && <Label>{label}</Label>}
      <select value={value} onChange={onChange}
        style={{ background:T.surfaceAlt, border:`1px solid ${T.border}`, borderRadius:4, padding:"10px 12px", fontSize:14, color:T.ink, fontFamily:"'Lato',sans-serif", outline:"none", width:"100%" }}>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}

function Card({ children, style, className="" }) {
  return <div className={className} style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:8, padding:"1.5rem", ...style }}>{children}</div>;
}

function SectionHead({ label, sub, delay="" }) {
  return (
    <div className={delay ? `fu${delay}` : ""} style={{ marginBottom:"1.25rem" }}>
      <h2 style={{ fontFamily:"'Playfair Display',serif", fontSize:22, fontWeight:600, letterSpacing:"-0.02em", marginBottom:2 }}>{label}</h2>
      {sub && <p style={{ fontSize:13, color:T.inkSec }}>{sub}</p>}
      <div style={{ width:40, height:2, background:T.green, marginTop:8 }} />
    </div>
  );
}

function ScoreBar({ label, value, max=10, color=T.green }) {
  return (
    <div style={{ marginBottom:10 }}>
      <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
        <span style={{ fontSize:13 }}>{label}</span>
        <span style={{ fontFamily:"'DM Mono',monospace", fontSize:13, color }}>{value}/{max}</span>
      </div>
      <div style={{ height:6, background:T.surfaceAlt, borderRadius:3, overflow:"hidden" }}>
        <div style={{ height:"100%", borderRadius:3, width:`${(value/max)*100}%`, background:color, transition:"width 1s ease" }} />
      </div>
    </div>
  );
}

function ModeBtn({ active, onClick, children }) {
  return (
    <button onClick={onClick} style={{ padding:"7px 16px", borderRadius:4, border:`1px solid ${active?T.ink:T.border}`, background:active?T.ink:"transparent", color:active?"#FFF":T.inkSec, fontSize:12, fontWeight:700, letterSpacing:"0.04em", textTransform:"uppercase", cursor:"pointer", fontFamily:"'Lato',sans-serif", transition:"all 0.15s" }}>
      {children}
    </button>
  );
}

function GoogleIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M17.64 9.2045c0-.638-.0573-1.252-.1636-1.8409H9v3.4814h4.8436c-.2086 1.125-.8427 2.0782-1.7959 2.7164v2.2581h2.9087c1.7018-1.5668 2.6836-3.874 2.6836-6.6150z" fill="#4285F4"/>
      <path d="M9 18c2.43 0 4.4673-.8059 5.9564-2.1805l-2.9087-2.2581c-.8059.54-1.8368.8582-3.0477.8582-2.3446 0-4.3282-1.5836-5.036-3.7104H.9574v2.3318C2.4382 15.9832 5.4818 18 9 18z" fill="#34A853"/>
      <path d="M3.964 10.71c-.18-.54-.2823-1.1168-.2823-1.71s.1023-1.17.2823-1.71V4.9582H.9574C.3477 6.1732 0 7.5477 0 9s.3477 2.8268.9574 4.0418L3.964 10.71z" fill="#FBBC05"/>
      <path d="M9 3.5795c1.3214 0 2.5077.4541 3.4405 1.346l2.5813-2.5813C13.4632.8918 11.4259 0 9 0 5.4818 0 2.4382 2.0168.9574 4.9582L3.964 7.29C4.6718 5.1632 6.6554 3.5795 9 3.5795z" fill="#EA4335"/>
    </svg>
  );
}

function TabBtn({ active, onClick, children }) {
  return (
    <button onClick={onClick} style={{ padding:"8px 18px", borderRadius:4, whiteSpace:"nowrap", border:`1px solid ${active?T.ink:T.border}`, background:active?T.ink:"transparent", color:active?"#FFF":T.inkSec, fontSize:12, fontWeight:700, letterSpacing:"0.04em", textTransform:"uppercase", cursor:"pointer", fontFamily:"'Lato',sans-serif", transition:"all 0.15s" }}>
      {children}
    </button>
  );
}

function PreferredStoresPanel({ userId, country }) {
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [removingId, setRemovingId] = useState(null);
  const [err, setErr] = useState(null);

  const fetchStores = useCallback(async () => {
    if (!userId || !country) { setLoading(false); return; }
    setLoading(true); setErr(null);
    try {
      const res = await fetch(`${BASE_URL}/preferred-stores?user_id=${encodeURIComponent(userId)}&country=${encodeURIComponent(country)}`);
      if (!res.ok) throw new Error(`Request failed (${res.status})`);
      const json = await res.json();
      setStores(Array.isArray(json) ? json : json.preferred_stores || []);
    } catch (e) {
      console.error("Failed to load preferred stores:", e);
      setErr("Couldn't load your preferred stores.");
    } finally {
      setLoading(false);
    }
  }, [userId, country]);

  useEffect(() => { fetchStores(); }, [fetchStores]);

  const handleRemove = async (storeId) => {
    if (!storeId) return;
    setRemovingId(storeId);
    try {
      const res = await fetch(`${BASE_URL}/preferred-store/${encodeURIComponent(storeId)}?user_id=${encodeURIComponent(userId)}`, { method:"DELETE" });
      if (!res.ok) throw new Error(`Remove failed (${res.status})`);
      const json = await res.json();
      setStores(json.preferred_stores || []);
    } catch (e) {
      console.error("Failed to remove preferred store:", e);
      setErr("Couldn't remove that store. Try again.");
    } finally {
      setRemovingId(null);
    }
  };

  if (!userId) return null;

  return (
    <Card style={{ marginBottom:"1.25rem" }}>
      <div style={{ display:"flex", alignItems:"baseline", justifyContent:"space-between", marginBottom:10 }}>
        <h3 style={{ fontFamily:"'Playfair Display',serif", fontSize:16, fontWeight:600, display:"flex", alignItems:"center", gap:6 }}>
          <span style={{ color:T.amber }}>★</span> Your Stores{country ? ` (${country})` : ""}
        </h3>
      </div>
      {loading ? (
        <p style={{ fontSize:13, color:T.inkSec }}>Loading…</p>
      ) : err ? (
        <p style={{ fontSize:13, color:T.red }}>{err}</p>
      ) : !stores.length ? (
        <p style={{ fontSize:13, color:T.inkSec }}>No preferred stores yet — upload a receipt and we'll remember the store for you.</p>
      ) : (
        <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
          {stores.map((s) => (
            <span key={s.store_id} style={{ display:"inline-flex", alignItems:"center", gap:6, background:T.amberLight, border:`1px solid ${T.amber}55`, borderRadius:20, padding:"5px 6px 5px 12px", fontSize:13 }}>
              <span style={{ color:T.amber }}>★</span>
              {s.store_name}
              <button
                onClick={() => handleRemove(s.store_id)}
                disabled={removingId === s.store_id}
                aria-label={`Remove ${s.store_name} from preferred stores`}
                style={{ border:"none", background:"transparent", cursor:"pointer", color:T.inkSec, fontSize:14, lineHeight:1, padding:"2px 4px", borderRadius:"50%" }}
              >
                {removingId === s.store_id ? "…" : "✕"}
              </button>
            </span>
          ))}
        </div>
      )}
    </Card>
  );
}

// ── Preferred Stores Page ────────────────────────────────────────────────────
function StoresPage({ user }) {
  const [allStores, setAllStores] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [err, setErr]             = useState(null);
  const [filter, setFilter]       = useState("ALL");
  const [removingId, setRemovingId] = useState(null);

  const userId = user?.uid;

  const fetchStores = useCallback(() => {
    if (!userId) { setLoading(false); return; }
    setLoading(true); setErr(null);
    fetch(`${BASE_URL}/preferred-stores?user_id=${encodeURIComponent(userId)}`)
      .then(r => { if (!r.ok) throw new Error(`Request failed (${r.status})`); return r.json(); })
      .then(json => setAllStores(Array.isArray(json) ? json : json.preferred_stores || []))
      .catch(e => { console.error("Failed to load preferred stores:", e); setErr("Couldn't load your preferred stores."); })
      .finally(() => setLoading(false));
  }, [userId]);

  useEffect(() => { fetchStores(); }, [fetchStores]);

  const handleRemove = async (storeId) => {
    if (!storeId || !userId) return;
    setRemovingId(storeId);
    try {
      const res = await fetch(`${BASE_URL}/preferred-store/${encodeURIComponent(storeId)}?user_id=${encodeURIComponent(userId)}`, { method:"DELETE" });
      if (!res.ok) throw new Error(`Remove failed (${res.status})`);
      const json = await res.json();
      setAllStores(json.preferred_stores || []);
    } catch (e) {
      console.error("Failed to remove preferred store:", e);
      setErr("Couldn't remove that store. Try again.");
    } finally {
      setRemovingId(null);
    }
  };

  const countries = ["ALL", ...Array.from(new Set(allStores.map(s => (s.country || "US").toUpperCase())))];
  const visible = filter === "ALL" ? allStores : allStores.filter(s => (s.country || "US").toUpperCase() === filter);

  return (
    <div style={{ maxWidth:700, margin:"0 auto", padding:"2rem 1.5rem" }}>
      <SectionHead label="Preferred Stores" sub="Stores you've marked as favorites — added automatically from receipt uploads" />

      {!userId ? (
        <Card><p style={{ color:T.inkSec }}>Sign in to manage your preferred stores.</p></Card>
      ) : (
        <>
          {countries.length > 2 && (
            <div style={{ display:"flex", gap:8, marginBottom:"1.25rem" }}>
              {countries.map(c => (
                <ModeBtn key={c} active={filter===c} onClick={()=>setFilter(c)}>{c}</ModeBtn>
              ))}
            </div>
          )}

          <Card>
            {loading ? (
              <p style={{ fontSize:13, color:T.inkSec }}>Loading…</p>
            ) : err ? (
              <p style={{ fontSize:13, color:T.red }}>{err}</p>
            ) : !visible.length ? (
              <p style={{ fontSize:13, color:T.inkSec }}>No preferred stores yet — upload a receipt and we'll remember the store for you, or come back here after your next shopping search.</p>
            ) : (
              <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                {visible.map(s => (
                  <div key={s.store_id} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"10px 14px", borderRadius:6, background:T.surfaceAlt, border:`1px solid ${T.border}` }}>
                    <div style={{ display:"flex", alignItems:"center", gap:10, minWidth:0 }}>
                      <span style={{ color:T.amber, fontSize:16, flexShrink:0 }}>★</span>
                      <div style={{ minWidth:0 }}>
                        <div style={{ fontSize:14, fontWeight:600, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{s.store_name}</div>
                        <div style={{ fontSize:12, color:T.inkSec }}>{[s.city, s.state, (s.country||"US").toUpperCase()].filter(Boolean).join(", ")}</div>
                      </div>
                    </div>
                    <button
                      onClick={() => handleRemove(s.store_id)}
                      disabled={removingId === s.store_id}
                      style={{ flexShrink:0, marginLeft:12, padding:"6px 12px", borderRadius:4, border:`1px solid ${T.border}`, background:"transparent", color:T.red, fontSize:12, fontWeight:700, cursor:removingId===s.store_id?"not-allowed":"pointer", fontFamily:"'Lato',sans-serif" }}
                    >
                      {removingId === s.store_id ? "Removing…" : "Remove"}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}

const US_STATES = [
  "Alabama","Alaska","Arizona","Arkansas","California","Colorado","Connecticut","Delaware",
  "District of Columbia","Florida","Georgia","Hawaii","Idaho","Illinois","Indiana","Iowa",
  "Kansas","Kentucky","Louisiana","Maine","Maryland","Massachusetts","Michigan","Minnesota",
  "Mississippi","Missouri","Montana","Nebraska","Nevada","New Hampshire","New Jersey",
  "New Mexico","New York","North Carolina","North Dakota","Ohio","Oklahoma","Oregon",
  "Pennsylvania","Rhode Island","South Carolina","South Dakota","Tennessee","Texas","Utah",
  "Vermont","Virginia","Washington","West Virginia","Wisconsin","Wyoming",
];

const IN_STATES = [
  "Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh","Goa","Gujarat",
  "Haryana","Himachal Pradesh","Jharkhand","Karnataka","Kerala","Madhya Pradesh",
  "Maharashtra","Manipur","Meghalaya","Mizoram","Nagaland","Odisha","Punjab","Rajasthan",
  "Sikkim","Tamil Nadu","Telangana","Tripura","Uttar Pradesh","Uttarakhand","West Bengal",
  "Andaman and Nicobar Islands","Chandigarh",
  "Dadra and Nagar Haveli and Daman and Diu","Delhi","Jammu and Kashmir","Ladakh",
  "Lakshadweep","Puducherry",
];

const STATES_BY_COUNTRY = { US: US_STATES, IN: IN_STATES };

/**
 * AutocompleteField — text input with debounced suggestions from the
 * backend's /location/autocomplete proxy (Google Places Autocomplete,
 * server-side key). Replaces raw free-text entry so people can search
 * instead of typing addresses/cities from scratch and risking typos.
 */
function AutocompleteField({ value, onChange, onSelect, mode, country, state, city, placeholder, disabled }) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);
  const blurTimeoutRef = useRef(null);

  const handleChange = (text) => {
    onChange(text);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (text.trim().length < 2) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ input: text, country: country || "US", mode: mode || "address" });
        if (state) params.set("state", state);
        if (city) params.set("city", city);
        const res = await fetch(`${BASE_URL}/location/autocomplete?${params.toString()}`);
        const json = await res.json();
        setSuggestions(json.predictions || []);
        setOpen(true);
      } catch (e) {
        console.error("Autocomplete failed:", e);
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 300);
  };

  const handleSelect = (prediction) => {
    onSelect(prediction.description);
    setSuggestions([]);
    setOpen(false);
  };

  return (
    <div style={{ position: "relative" }}>
      <input
        type="text"
        value={value}
        disabled={disabled}
        onChange={e => handleChange(e.target.value)}
        onFocus={() => { if (suggestions.length) setOpen(true); }}
        onBlur={() => { blurTimeoutRef.current = setTimeout(() => setOpen(false), 150); }}
        placeholder={disabled ? "Select a state first" : placeholder}
        style={{
          width: "100%", background: disabled ? T.surfaceAlt2 || T.surfaceAlt : T.surfaceAlt,
          border: `1px solid ${T.border}`, borderRadius: 4, padding: "10px 12px", fontSize: 14,
          boxSizing: "border-box", fontFamily: "'Lato',sans-serif",
          cursor: disabled ? "not-allowed" : "text", opacity: disabled ? 0.6 : 1,
        }}
      />
      {loading && (
        <span style={{ position: "absolute", right: 10, top: 10, fontSize: 12, color: T.subtext }}>…</span>
      )}
      {open && suggestions.length > 0 && (
        <div style={{
          position: "absolute", top: "calc(100% + 2px)", left: 0, right: 0, zIndex: 20,
          background: T.surface, border: `1px solid ${T.border}`, borderRadius: 4,
          boxShadow: "0 4px 12px rgba(0,0,0,0.12)", maxHeight: 220, overflowY: "auto"
        }}>
          {suggestions.map(s => (
            <div
              key={s.place_id || s.description}
              onMouseDown={() => handleSelect(s)}
              style={{ padding: "9px 12px", fontSize: 13, cursor: "pointer", borderBottom: `1px solid ${T.border}` }}
              onMouseEnter={e => e.currentTarget.style.background = T.surfaceAlt}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}
            >
              {s.description}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const DAYS  = ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"];
// Keep MEAL_TYPES adjacent to DAYS because both drive the 7-day meal planner grid.
const MEAL_TYPES = ["Breakfast", "Lunch", "Dinner"];
// DIETS drives both the multi-select chips and the canonical order used when
// joining selections into the free-text dietary_instruction string sent to
// the backend (pipeline.py treats it as an opaque string that also becomes
// part of the recipe cache key — so a stable, sorted order matters: the same
// set of diets must always serialize to the same string, or cache lookups
// for "Vegetarian, Keto" vs "Keto, Vegetarian" would silently miss).
const DIETS = ["Vegetarian","Vegan","Pescatarian","Keto","Paleo","High Protein","Low Carb","Gluten-Free","Dairy-Free"];

// Turns a selected-diets array into the single string pipeline.py's
// dietary_instruction expects. Empty selection maps to "None" — same
// no-restriction meaning the old single-select's default carried.
function formatDietaryInstruction(selected) {
  const chosen = (selected || []).filter(d => DIETS.includes(d));
  if (!chosen.length) return "None";
  return DIETS.filter(d => chosen.includes(d)).join(", ");
}

// Old saved profiles have `dietary` as a single string (e.g. "Keto" or
// "None"); newer ones may already be an array. Normalize either shape into
// an array of selected diets for the multi-select UI.
function parseDietaryPreferences(value) {
  if (Array.isArray(value)) return value.filter(d => DIETS.includes(d));
  if (typeof value === "string" && value && value !== "None") {
    return value.split(",").map(s => s.trim()).filter(d => DIETS.includes(d));
  }
  return [];
}
const TABS  = [
  { id:"list",      label:"Shopping List" },
  { id:"stores",    label:"Store Prices"  },
  { id:"route",     label:"Route"         },
  { id:"nutrition", label:"Nutrition"     },
  { id:"subs",      label:"Substitutions" },
];

const normalizeLookupText = (value) => String(value || "")
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "")
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, " ")
  .trim();

function extractPostalCode(text, country = "US") {
  const value = String(text || "").trim();
  if (!value) return "";
  if (country === "IN") {
    const pin = value.match(/\b(\d{6})\b/);
    return pin ? pin[1] : "";
  }
  const zip = value.match(/\b(\d{5}(?:-\d{4})?)\b/);
  return zip ? zip[1] : "";
}

function hasShoppingPostalCode(postalCode) {
  return !!String(postalCode || "").trim();
}

// Builds the manual_* location fields the backend expects, sourced directly
// from a freshly-fetched profile object (not React state, which may not have
// re-rendered yet right after a setter call in the same function).
function buildLocationPayloadFrom(p = {}) {
  return hasShoppingPostalCode(p.postalCode)
    ? {
        manual_city: p.city || "",
        manual_state: p.state || "",
        manual_postal_code: String(p.postalCode || "").trim(),
        manual_country: p.country || "US",
        manual_address: String(p.address || "").trim(),
      }
    : {};
}

function locationPartIncluded(text, part) {
  const haystack = String(text || "").toLowerCase();
  const needle = String(part || "").trim().toLowerCase();
  return !!needle && haystack.includes(needle);
}

function formatProfileLocationLabel(profile) {
  const address = String(profile.address || "").trim();
  const city = String(profile.city || "").trim();
  const state = String(profile.state || "").trim();
  const postalCode = String(profile.postalCode || "").trim();
  const trailingParts = [city, state, postalCode].filter(
    part => part && !locationPartIncluded(address, part)
  );

  if (address && trailingParts.length) return `${address}, ${trailingParts.join(", ")}`;
  if (address) return address;
  if (trailingParts.length) return trailingParts.join(", ");
  return postalCode;
}

const CHEF_OCCUPATION_IDS = new Set([
  "Q3499072",  // chef
  "Q10800557", // cook
  "Q131512",   // restaurateur
  "Q3349513",  // pastry chef
  "Q1925963",  // baker
  "Q214917",   // food writer
]);

function hasChefSignal(entity) {
  const description = entity?.descriptions?.en?.value || "";
  const descriptionLooksRelevant = /\b(chef|cook|restaurateur|baker|culinarian|food writer|pastry)\b/i.test(description);
  const occupationClaims = entity?.claims?.P106 || [];
  const occupationLooksRelevant = occupationClaims.some(claim => {
    const id = claim?.mainsnak?.datavalue?.value?.id;
    return id && CHEF_OCCUPATION_IDS.has(id);
  });
  return descriptionLooksRelevant || occupationLooksRelevant;
}

async function verifyChefNameOnline(name, getToken) {
  const query = String(name || "").trim();
  if (!query) return { ok:false, message:"Enter a chef name to verify." };

  try {
    const searchParams = new URLSearchParams({
      action:"wbsearchentities",
      search:query,
      language:"en",
      format:"json",
      origin:"*",
      limit:"8",
    });
    const searchRes = await fetch(`https://www.wikidata.org/w/api.php?${searchParams.toString()}`);
    if (!searchRes.ok) throw new Error("Chef verification lookup failed.");
    const searchJson = await searchRes.json();
    const candidates = Array.isArray(searchJson.search) ? searchJson.search.filter(item => item?.id) : [];
    if (candidates.length) {
      const entityParams = new URLSearchParams({
        action:"wbgetentities",
        ids:candidates.map(item => item.id).join("|"),
        props:"labels|descriptions|claims",
        languages:"en",
        format:"json",
        origin:"*",
      });
      const entityRes = await fetch(`https://www.wikidata.org/w/api.php?${entityParams.toString()}`);
      if (!entityRes.ok) throw new Error("Chef verification lookup failed.");
      const entityJson = await entityRes.json();
      const matched = candidates
        .map(candidate => entityJson.entities?.[candidate.id])
        .find(entity => entity && hasChefSignal(entity));

      if (matched) {
        const canonicalName = matched.labels?.en?.value || query;
        return {
          ok:true,
          name:canonicalName,
          id:matched.id,
          source:`https://www.wikidata.org/wiki/${matched.id}`,
        };
      }
    }
  } catch (wikidataError) {
    console.warn("Wikidata chef verification failed, falling back to Gemini.", wikidataError);
  }

  const token = typeof getToken === "function" ? await getToken() : null;
  const res = await fetch(`${BASE_URL}/verify-chef`, {
    method:"POST",
    headers:{ "Content-Type":"application/json", ...(token ? { Authorization:`Bearer ${token}` } : {}) },
    body: JSON.stringify({ name:query }),
  });
  const json = await res.json().catch(()=>({}));
  if (!res.ok) throw new Error(apiErrorMessage(json, "Chef verification failed. Try again."));
  if (!json?.ok) {
    return { ok:false, message:json?.message || "Chef not found. Change the name or remove it before saving." };
  }
  return {
    ok:true,
    name:json.name || query,
    source:json.source === "gemini" ? null : json.source,
  };
}

const CHAT_SUGGESTIONS = [
  "Plan a meal from my pantry",
  "Find the cheapest store nearby",
  "Show me my saved recipes",
];

const headerBtnStyle = {
  display:"flex", alignItems:"center", gap:8,
  padding:"8px 16px", borderRadius:6,
  border:"1px solid rgba(255,255,255,0.2)",
  background:"rgba(255,255,255,0.1)",
  color:"#FFF", fontSize:12, fontWeight:700,
  letterSpacing:"0.04em", textTransform:"uppercase",
  cursor:"pointer", fontFamily:"'Lato',sans-serif",
  transition:"all 0.15s",
};

// ── Price matrix ─────────────────────────────────────────────────────────────
function buildMatrix(stores) {
  const allItems = new Set();
  stores.forEach(s => (s.items||[]).forEach(i => allItems.add(i.item)));
  const items = [...allItems].sort();
  const matrix = {}, totals = {}, currencies = {}, sources = {}, notes = {}, priceStores = {}, measurements = {};
  stores.forEach(s => {
    const n = s.store_name; totals[n]=0; matrix[n]={}; sources[n]={}; notes[n]={}; priceStores[n]={}; measurements[n]={};
    (s.items||[]).forEach(i => {
      matrix[n][i.item]=i.price;
      sources[n][i.item]=i.source || "estimate";
      notes[n][i.item]=i.note || (i.source === "receipt" ? "Verified price from a recent receipt" : "Estimated price");
      priceStores[n][i.item]=i.price_store || n;
      measurements[n][i.item]=i.measurement || "";
      totals[n]+=i.price;
      if (!currencies[n]) currencies[n] = i.currency || "USD";
    });
  });
  const wCounts={}, wTotals={};
  items.forEach(item => {
    let minP=Infinity, minS=null;
    stores.forEach(s => { const p=matrix[s.store_name]?.[item]; if(p!==undefined&&p<minP){minP=p;minS=s.store_name;} });
    if(minS){ wCounts[minS]=(wCounts[minS]||0)+1; wTotals[minS]=(wTotals[minS]||0)+minP; }
  });
  return { items, matrix, totals, currencies, sources, notes, priceStores, measurements, wCounts, wTotals, overallCheapest:Object.values(wTotals).reduce((a,b)=>a+b,0) };
}

const CURRENCY_SYMBOLS = { USD:"$", INR:"₹", GBP:"£", CAD:"CA$", AUD:"A$", SGD:"S$", AED:"AED " };
function formatPrice(amount, currency="USD") {
  return `${CURRENCY_SYMBOLS[currency] || ""}${amount.toFixed(2)}`;
}

function pantryRow(item) {
  if (typeof item === "string") return { name:item, qty:"", weight:"" };
  return {
    name:item?.name || item?.item || "",
    qty:item?.qty ?? item?.quantity ?? "",
    weight:item?.weight || item?.measurement || "",
  };
}

function mealNameOnly(value="") {
  return String(value || "").split("|")[0].trim();
}

function apiErrorMessage(json={}, fallback="Request failed") {
  const detail = json?.detail;
  if (Array.isArray(detail)) {
    const message = detail.map(item => {
      if (!item || typeof item !== "object") return String(item);
      const loc = Array.isArray(item.loc) ? item.loc.join(".") : item.loc;
      return `${loc ? `${loc}: ` : ""}${item.msg || JSON.stringify(item)}`;
    }).join("; ");
    if (message) return message;
  }
  if (typeof detail === "string" && detail) return detail;
  const error = json?.error;
  if (typeof error === "string" && error) return error;
  if (error && typeof error === "object") return JSON.stringify(error);
  return fallback;
}

async function loadSavedPlanningProfile(getToken) {
  const token = await getToken();
  const res = await fetch(`${BASE_URL}/profile/me`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(apiErrorMessage(json, "Could not load saved profile"));
  return json?.profile ?? {};
}

function applySavedProfileToState(savedProfile, setters) {
  setters.setDietary(savedProfile.dietary || "None");
  setters.setPreferredChef(savedProfile.preferredChef || "");
  setters.setManualAddress(savedProfile.address || "");
  setters.setManualCity(savedProfile.city || "");
  setters.setManualState(savedProfile.state || "");
  setters.setManualPostalCode(savedProfile.postalCode || "");
  setters.setManualCountry(savedProfile.country || "US");
  setters.setManualLocationSet(hasShoppingPostalCode(savedProfile.postalCode));
}

function weekPlanToResults(plan={}) {
  return {
    shopping_list: plan.shopping_list || plan.combined_shopping_list || [],
    recommended_stores: plan.recommended_stores || [],
    optimized_route: plan.optimized_route || [],
    nutrition_report: plan.nutrition_report || {},
    substitutions: plan.substitutions || {},
    price_sources: plan.price_sources || {},
    agent_trace: plan.steps || [],
  };
}

// ── Chat Panel ───────────────────────────────────────────────────────────────
function toolLabel(name="") {
  const labels = {
    get_pantry_items: "Pantry inventory",
    list_recipes: "Saved recipes",
    compare_stores: "Store comparison",
  };
  return labels[name] || name.replace(/_/g, " ") || "Tool";
}

function toolIcon(step) {
  if (step?.status === "error") return "⚠️";
  if (step?.tool === "get_pantry_items") return "🥦";
  if (step?.tool === "list_recipes") return "📖";
  if (step?.tool === "compare_stores") return "🏪";
  return "✅";
}

function moneyMaybe(value, currency="USD") {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return formatPrice(n, currency);
}

function AgentTrace({ steps=[] }) {
  const [open, setOpen] = useState(false);
  if (!steps?.length) return null;
  const failures = steps.filter(s => s.status === "error").length;
  return (
    <div style={{ marginTop:8, border:`1px solid ${T.border}`, borderRadius:8, overflow:"hidden", background:T.surface }}>
      <button onClick={()=>setOpen(o=>!o)} style={{ width:"100%", display:"flex", alignItems:"center", justifyContent:"space-between", gap:8, padding:"7px 9px", border:"none", background:T.surfaceAlt, color:T.inkSec, cursor:"pointer", fontSize:11, fontWeight:700, letterSpacing:"0.04em", textTransform:"uppercase", fontFamily:"'Lato',sans-serif" }}>
        <span>{failures ? "⚠️" : "✅"} Agent used tools · {steps.length}</span>
        <span style={{ fontSize:13 }}>{open ? "▴" : "▾"}</span>
      </button>
      {open && (
        <div style={{ padding:"8px 10px", display:"flex", flexDirection:"column", gap:8 }}>
          {steps.map((step, i) => (
            <div key={`${step.tool}-${i}`} style={{ display:"grid", gridTemplateColumns:"22px 1fr", gap:8, alignItems:"start" }}>
              <span style={{ fontSize:15, lineHeight:"20px" }}>{toolIcon(step)}</span>
              <div>
                <div style={{ fontSize:12, fontWeight:700, color:step.status==="error"?T.red:T.ink, textTransform:"capitalize" }}>{toolLabel(step.tool)}</div>
                <div style={{ fontSize:11, color:step.status==="error"?T.red:T.inkSec, lineHeight:1.4 }}>{step.summary}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AgentCards({ cards={} }) {
  const shopping = Array.isArray(cards.shopping_list) ? cards.shopping_list : [];
  const stores = Array.isArray(cards.stores) ? cards.stores : [];
  const recipes = Array.isArray(cards.recipes) ? cards.recipes : [];
  if (!shopping.length && !stores.length && !recipes.length) return null;

  return (
    <div style={{ marginTop:8, display:"flex", flexDirection:"column", gap:8 }}>
      {!!shopping.length && (
        <div style={{ border:`1px solid ${T.green}33`, background:T.greenLight, borderRadius:8, padding:"9px 10px" }}>
          <div style={{ fontSize:11, fontWeight:800, color:T.green, letterSpacing:"0.05em", textTransform:"uppercase", marginBottom:6 }}>Items</div>
          <div style={{ display:"flex", flexWrap:"wrap", gap:5 }}>
            {shopping.slice(0, 12).map((item, i) => (
              <span key={`${item}-${i}`} style={{ fontSize:11, padding:"3px 7px", borderRadius:14, background:T.surface, color:T.green, border:`1px solid ${T.green}22`, textTransform:"capitalize" }}>{item}</span>
            ))}
            {shopping.length > 12 && <span style={{ fontSize:11, color:T.green, padding:"3px 0" }}>+{shopping.length-12} more</span>}
          </div>
        </div>
      )}

      {!!stores.length && (
        <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
          <div style={{ fontSize:11, fontWeight:800, color:T.inkSec, letterSpacing:"0.05em", textTransform:"uppercase" }}>Store comparison</div>
          {stores.slice(0, 3).map((store, i) => {
            const breakdown = Object.values(store.price_breakdown||{});
            const currency = store.items?.[0]?.currency || breakdown?.[0]?.currency || "USD";
            const price = moneyMaybe(store.basket_price, currency);
            return (
              <div key={`${store.store_name || store.brand || "store"}-${i}`} style={{ border:`1px solid ${T.border}`, background:T.surface, borderRadius:8, padding:"8px 10px" }}>
                <div style={{ display:"flex", justifyContent:"space-between", gap:8, alignItems:"baseline" }}>
                  <span style={{ fontSize:12, fontWeight:800, color:T.ink }}>{store.store_name || store.brand || "Store"}</span>
                  {price && <span style={{ fontSize:12, fontFamily:"'DM Mono',monospace", color:T.green }}>{price}</span>}
                </div>
                <div style={{ display:"flex", gap:10, marginTop:3, fontSize:11, color:T.inkSec }}>
                  {Number.isFinite(Number(store.distance_km)) && <span>📍 {Number(store.distance_km).toFixed(1)} km</span>}
                  {Number.isFinite(Number(store.final_score)) && <span>Score {Number(store.final_score).toFixed(2)}</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!!recipes.length && (
        <div style={{ border:`1px solid ${T.blue}22`, background:T.blueLight, borderRadius:8, padding:"9px 10px" }}>
          <div style={{ fontSize:11, fontWeight:800, color:T.blue, letterSpacing:"0.05em", textTransform:"uppercase", marginBottom:6 }}>Recipes</div>
          <div style={{ display:"flex", flexWrap:"wrap", gap:5 }}>
            {recipes.slice(0, 10).map((recipe, i) => {
              const raw = recipe.meal || recipe.name || `Recipe ${i+1}`;
              const label = String(raw).split("|")[0];
              return <span key={`${raw}-${i}`} style={{ fontSize:11, padding:"3px 7px", borderRadius:14, background:T.surface, color:T.blue, border:`1px solid ${T.blue}22`, textTransform:"capitalize" }}>{label}</span>;
            })}
            {recipes.length > 10 && <span style={{ fontSize:11, color:T.blue, padding:"3px 0" }}>+{recipes.length-10} more</span>}
          </div>
        </div>
      )}
    </div>
  );
}

function ChatPanel({ onClose, user, getToken }) {
  const [messages, setMessages] = useState([
    { role:"agent", text:"Hi! I'm your SmartCart AI assistant. I can check your pantry, suggest meals, and help plan your groceries. What would you like to know?", steps:[], cards:{ shopping_list:[], stores:[], recipes:[] } }
  ]);
  const [input, setInput] = useState("");
  const [loading, setChatLoading] = useState(false);
  const [sessionId] = useState(() => `session_${Date.now()}`);
  const messagesEndRef = useRef(null);
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior:"smooth" }); }, [messages]);

  const sendMessage = async (text) => {
    const msg = text || input.trim();
    if (!msg) return;
    setMessages(prev => [...prev, { role:"user", text:msg }]);
    setInput(""); setChatLoading(true);
    try {
      const token = await getToken();
      const res = await fetch(AGENT_URL, {
        method:"POST",
        headers:{ "Content-Type":"application/json", ...(token?{Authorization:`Bearer ${token}`}:{}) },
        body: JSON.stringify({ message:msg, user_id:user?.uid??"anonymous", session_id:sessionId }),
      });
      const json = await res.json();
      setMessages(prev => [...prev, {
        role:"agent",
        text:json.response||"Sorry, I didn't get a response.",
        steps:Array.isArray(json.steps)?json.steps:[],
        cards:json.cards||{ shopping_list:[], stores:[], recipes:[] },
        usage:json.usage||null,
      }]);
    } catch {
      setMessages(prev => [...prev, { role:"agent", text:"⚠ Connection error. Please try again.", steps:[], cards:{ shopping_list:[], stores:[], recipes:[] }, isError:true }]);
    } finally { setChatLoading(false); }
  };

  return (
    <div className="chat-panel" style={{ position:"fixed", top:0, right:0, width:380, height:"100vh", background:T.surface, borderLeft:`1px solid ${T.border}`, display:"flex", flexDirection:"column", zIndex:1000, boxShadow:"-4px 0 24px rgba(0,0,0,0.08)" }}>
      <div style={{ padding:"1rem 1.25rem", borderBottom:`1px solid ${T.border}`, display:"flex", alignItems:"center", justifyContent:"space-between", background:T.ink, color:"#FFF", flexShrink:0 }}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <span style={{ fontSize:20 }}>🤖</span>
          <div>
            <div style={{ fontFamily:"'Playfair Display',serif", fontSize:15 }}>SmartCart <em style={{ fontWeight:400, color:T.greenMid }}>Agent</em></div>
            <div style={{ fontSize:10, opacity:0.5, letterSpacing:"0.08em", textTransform:"uppercase" }}>Powered by Gemini + ADK</div>
          </div>
        </div>
        <button onClick={onClose} style={{ background:"transparent", border:"none", color:"#FFF", fontSize:20, cursor:"pointer", opacity:0.7, padding:"4px 8px", borderRadius:4 }}>✕</button>
      </div>
      <div style={{ flex:1, overflowY:"auto", padding:"1rem", display:"flex", flexDirection:"column", gap:12 }}>
        {messages.map((msg,i) => (
          <div key={i} className={msg.role==="user"?"chat-msg-user":"chat-msg-agent"} style={{ display:"flex", justifyContent:msg.role==="user"?"flex-end":"flex-start" }}>
            <div style={{ maxWidth:"85%" }}>
              <div style={{ padding:"10px 14px", borderRadius:msg.role==="user"?"12px 12px 2px 12px":"12px 12px 12px 2px", background:msg.role==="user"?T.ink:msg.isError?T.redLight:T.surfaceAlt, color:msg.role==="user"?"#FFF":msg.isError?T.red:T.ink, fontSize:13, lineHeight:1.6, border:`1px solid ${msg.role==="user"?"transparent":msg.isError?T.red+"33":T.border}` }}>
                {msg.text}
              </div>
              {msg.role==="agent" && !msg.isError && <AgentTrace steps={msg.steps||[]} />}
              {msg.role==="agent" && !msg.isError && <AgentCards cards={msg.cards||{}} />}
            </div>
          </div>
        ))}
        {loading && <div style={{ display:"flex", justifyContent:"flex-start" }}><div style={{ padding:"10px 14px", borderRadius:"12px 12px 12px 2px", background:T.surfaceAlt, border:`1px solid ${T.border}`, display:"flex", gap:5, alignItems:"center" }}>{[0,1,2].map(i=><div key={i} style={{ width:6, height:6, borderRadius:"50%", background:T.inkTer, animation:`pulse 1.2s ${i*0.2}s ease infinite` }}/>)}</div></div>}
        <div ref={messagesEndRef}/>
      </div>
      <div style={{ padding:"0 1rem 0.75rem", display:"flex", flexWrap:"wrap", gap:6, flexShrink:0 }}>
        {CHAT_SUGGESTIONS.map((s,i) => <button key={i} onClick={()=>sendMessage(s)} disabled={loading} style={{ fontSize:11, padding:"4px 10px", borderRadius:20, border:`1px solid ${T.border}`, background:T.surfaceAlt, color:T.inkSec, cursor:loading?"not-allowed":"pointer", fontFamily:"'Lato',sans-serif", fontWeight:700 }}>{s}</button>)}
      </div>
      <div style={{ padding:"0.75rem 1rem", borderTop:`1px solid ${T.border}`, display:"flex", gap:8, flexShrink:0, background:T.surface }}>
        <textarea value={input} onChange={e=>setInput(e.target.value)} onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();sendMessage();}}} placeholder="Ask about your pantry, meals, or grocery list…" rows={2} style={{ flex:1, background:T.surfaceAlt, border:`1px solid ${T.border}`, borderRadius:6, padding:"8px 12px", fontSize:13, color:T.ink, fontFamily:"'Lato',sans-serif", outline:"none", resize:"none", lineHeight:1.5 }}/>
        <button onClick={()=>sendMessage()} disabled={loading||!input.trim()} style={{ width:40, height:40, alignSelf:"flex-end", borderRadius:6, background:loading||!input.trim()?T.borderDark:T.green, border:"none", color:"#FFF", fontSize:16, cursor:loading||!input.trim()?"not-allowed":"pointer", display:"flex", alignItems:"center", justifyContent:"center", transition:"background 0.15s", flexShrink:0 }}>↑</button>
      </div>
    </div>
  );
}

// ── Pantry Page ──────────────────────────────────────────────────────────────
function PantryPage({ getToken, onSaved }) {
  const [items, setItems]       = useState([]);   // [{name, qty, weight}]
  const [input, setInput]       = useState("");
  const [inputQty, setInputQty] = useState("");
  const [inputWeight, setInputWeight] = useState("");
  const [mergeMode, setMerge]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [msg, setMsg]           = useState(null);
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef(null);
  const fileRef = useRef(null);
  const hasSpeech = typeof window !== "undefined" && ("SpeechRecognition" in window || "webkitSpeechRecognition" in window);

  // Load pantry on mount
  useEffect(() => {
    getToken().then(token =>
      fetch(`${BASE_URL}/debug/pantry/me`, {
        headers: token ? { Authorization:`Bearer ${token}` } : {}
      }).then(r=>r.json()).then(json => {
        const raw = json?.result?.items ?? [];
        // Normalize in case any legacy string-only items are still stored
        const normalized = (Array.isArray(raw) ? raw : []).map(it =>
          typeof it === "string" ? { name: it, qty: "", weight: "" } : it
        );
        setItems(normalized);
      }).catch(()=>{})
    );
  }, [getToken]);

  const startListening = () => {
    if (!hasSpeech) return;
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const rec = new SR();
    rec.lang = "en-US";
    rec.interimResults = false;
    rec.maxAlternatives = 1;
    rec.onresult = (e) => {
      const transcript = e.results[0][0].transcript;
      setInput(transcript);
      setListening(false);
    };
    rec.onerror = () => setListening(false);
    rec.onend   = () => setListening(false);
    recognitionRef.current = rec;
    rec.start();
    setListening(true);
  };

  const stopListening = () => {
    recognitionRef.current?.stop();
    setListening(false);
  };

  const addItem = () => {
    const name = input.trim().toLowerCase();
    if (!name) return;
    setItems(prev => {
      if (prev.some(it => it.name === name)) return prev; // already exists
      return [...prev, { name, qty: inputQty.trim(), weight: inputWeight.trim() }];
    });
    setInput(""); setInputQty(""); setInputWeight("");
  };

  const removeItem = (name) => setItems(prev => prev.filter(it => it.name !== name));

  const updateItemField = (name, field, value) => {
    setItems(prev => prev.map(it => it.name === name ? { ...it, [field]: value } : it));
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target.result;
      const parsedNames = text.split(/[\n,]+/).map(s=>s.trim().toLowerCase()).filter(Boolean);
      const parsed = parsedNames.map(name => ({ name, qty: "", weight: "" }));
      setItems(prev => {
        if (!mergeMode) return parsed;
        const byName = new Map(prev.map(it => [it.name, it]));
        for (const p of parsed) if (!byName.has(p.name)) byName.set(p.name, p);
        return [...byName.values()];
      });
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const savePantry = async () => {
    setSaving(true); setMsg(null);
    try {
      const token = await getToken();
      const res = await fetch(`${BASE_URL}/pantry`, {
        method:"PUT",
        headers:{ "Content-Type":"application/json", ...(token?{Authorization:`Bearer ${token}`}:{}) },
        body: JSON.stringify({ items }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json?.success === false) {
        throw new Error(json?.detail || json?.error || "Save failed");
      }
      const savedItems = json?.result?.items;
      if (Array.isArray(savedItems)) setItems(savedItems);
      onSaved?.(savedItems || items);
      setMsg({ type:"success", text:`✓ Pantry saved — ${(savedItems || items).length} items` });
    } catch (e) {
      setMsg({ type:"error", text:`⚠ ${e.message}` });
    } finally { setSaving(false); }
  };

  const inputStyle = { width:"100%", background:T.surfaceAlt, border:`1px solid ${T.border}`, borderRadius:4, padding:"10px 12px", fontSize:14, color:T.ink, fontFamily:"'Lato',sans-serif", outline:"none", boxSizing:"border-box" };

  return (
    <div style={{ maxWidth:700, margin:"0 auto", padding:"2rem 1.5rem" }}>
      <SectionHead label="My Pantry" sub="Manage items you already have at home" />

      <Card style={{ marginBottom:"1.5rem" }}>
        <Label>Add Item</Label>
        <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr 1fr auto auto", gap:8, marginBottom:12 }}>
          <input
            value={input}
            onChange={e=>setInput(e.target.value)}
            onKeyDown={e=>{ if(e.key==="Enter") addItem(); }}
            placeholder="Item name…"
            style={inputStyle}
          />
          <input
            value={inputQty}
            onChange={e=>setInputQty(e.target.value)}
            onKeyDown={e=>{ if(e.key==="Enter") addItem(); }}
            placeholder="Qty"
            style={inputStyle}
          />
          <input
            value={inputWeight}
            onChange={e=>setInputWeight(e.target.value)}
            onKeyDown={e=>{ if(e.key==="Enter") addItem(); }}
            placeholder="Weight"
            style={inputStyle}
          />
          {hasSpeech && (
            <button
              onClick={listening ? stopListening : startListening}
              className={listening ? "mic-active" : ""}
              title={listening ? "Stop listening" : "Speak to add item"}
              style={{ width:44, height:44, borderRadius:6, border:"none", background:listening?T.red:T.surfaceAlt, color:listening?"#FFF":T.inkSec, fontSize:18, cursor:"pointer", flexShrink:0, transition:"background 0.15s" }}
            >🎤</button>
          )}
          <button onClick={addItem} style={{ padding:"10px 18px", borderRadius:4, border:"none", background:T.green, color:"#FFF", fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"'Lato',sans-serif", flexShrink:0 }}>Add</button>
        </div>

        {!hasSpeech && (
          <p style={{ fontSize:11, color:T.inkSec, marginBottom:12 }}>💡 Use Chrome or Edge for voice input support.</p>
        )}

        <div style={{ display:"flex", alignItems:"center", gap:16, marginBottom:8 }}>
          <button onClick={()=>fileRef.current?.click()} style={{ fontSize:12, padding:"7px 14px", borderRadius:4, border:`1px solid ${T.border}`, background:T.surfaceAlt, color:T.inkSec, cursor:"pointer", fontFamily:"'Lato',sans-serif", fontWeight:700 }}>
            📁 Upload CSV / TXT
          </button>
          <input ref={fileRef} type="file" accept=".csv,.txt" onChange={handleFileUpload} style={{ display:"none" }}/>
          <label style={{ display:"flex", alignItems:"center", gap:6, fontSize:12, color:T.inkSec, cursor:"pointer" }}>
            <input type="checkbox" checked={mergeMode} onChange={e=>setMerge(e.target.checked)} />
            Merge with existing (uncheck to replace)
          </label>
        </div>
      </Card>

      <Card style={{ marginBottom:"1.5rem" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"1rem" }}>
          <Label>Current Pantry ({items.length} items)</Label>
          {items.length > 0 && (
            <button onClick={()=>setItems([])} style={{ fontSize:11, color:T.red, background:"none", border:"none", cursor:"pointer", fontWeight:700 }}>Clear all</button>
          )}
        </div>
        {!items.length ? (
          <p style={{ color:T.inkSec, fontSize:13 }}>No pantry items yet. Add some above.</p>
        ) : (
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
            <thead>
              <tr style={{ borderBottom:`1px solid ${T.border}` }}>
                <th style={{ textAlign:"left", padding:"6px 8px", color:T.inkSec, fontWeight:700 }}>Item</th>
                <th style={{ textAlign:"left", padding:"6px 8px", color:T.inkSec, fontWeight:700 }}>Qty</th>
                <th style={{ textAlign:"left", padding:"6px 8px", color:T.inkSec, fontWeight:700 }}>Weight</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map(it => (
                <tr key={it.name} style={{ borderBottom:`1px solid ${T.border}` }}>
                  <td style={{ padding:"6px 8px", textTransform:"capitalize" }}>{it.name}</td>
                  <td style={{ padding:"6px 8px" }}>
                    <input
                      value={it.qty ?? ""}
                      onChange={e=>updateItemField(it.name, "qty", e.target.value)}
                      style={{ width:60, background:T.surfaceAlt, border:`1px solid ${T.border}`, borderRadius:4, padding:"4px 6px", fontSize:12 }}
                    />
                  </td>
                  <td style={{ padding:"6px 8px" }}>
                    <input
                      value={it.weight ?? ""}
                      onChange={e=>updateItemField(it.name, "weight", e.target.value)}
                      style={{ width:80, background:T.surfaceAlt, border:`1px solid ${T.border}`, borderRadius:4, padding:"4px 6px", fontSize:12 }}
                    />
                  </td>
                  <td style={{ padding:"6px 8px", textAlign:"right" }}>
                    <button onClick={()=>removeItem(it.name)} style={{ background:"none", border:"none", color:T.red, cursor:"pointer", fontSize:16, lineHeight:1, padding:0, fontWeight:700 }}>×</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <button onClick={savePantry} disabled={saving} style={{ width:"100%", padding:"14px", background:saving?T.borderDark:T.ink, color:"#FFF", border:"none", borderRadius:4, fontSize:14, fontWeight:700, letterSpacing:"0.04em", textTransform:"uppercase", cursor:saving?"not-allowed":"pointer", fontFamily:"'Lato',sans-serif", transition:"background 0.15s" }}>
        {saving ? "Saving…" : "💾 Save Pantry to Profile"}
      </button>

      {msg && (
        <div style={{ marginTop:12, padding:"10px 14px", borderRadius:6, fontSize:13, fontWeight:500, background:msg.type==="success"?T.greenLight:T.redLight, color:msg.type==="success"?T.green:T.red, border:`1px solid ${msg.type==="success"?T.green+"33":T.red+"33"}` }}>
          {msg.text}
        </div>
      )}
    </div>
  );
}

// ── Feedback Page ────────────────────────────────────────────────────────────
function FeedbackPage({ getToken }) {
  const [email,   setEmail]   = useState("");
  const [comment, setComment] = useState("");
  const [sending, setSending] = useState(false);
  const [msg,     setMsg]     = useState(null);

  const submitFeedback = async () => {
    if (!comment.trim()) {
      setMsg({ type:"error", text:"⚠ Please enter your feedback before submitting." });
      return;
    }
    setSending(true); setMsg(null);
    try {
      const token = await getToken();
      const res = await fetch(`${BASE_URL}/feedback`, {
        method:"POST",
        headers:{ "Content-Type":"application/json", ...(token?{Authorization:`Bearer ${token}`}:{}) },
        body: JSON.stringify({ email, comment }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(apiErrorMessage(json, "Failed to send feedback"));
      setMsg({ type:"success", text:"✓ Thank you! Your feedback has been sent." });
      setComment("");
    } catch (e) {
      setMsg({ type:"error", text:`⚠ ${e.message}` });
    } finally { setSending(false); }
  };

  return (
    <div style={{ maxWidth:700, margin:"0 auto", padding:"2rem 1.5rem" }}>
      <SectionHead label="Feedback" sub="Share ideas, report issues, or tell us what's working" />

      <Card style={{ marginBottom:"1.5rem" }}>
        <Label>Your Email (so we can follow up)</Label>
        <input
          type="email"
          value={email}
          onChange={e=>setEmail(e.target.value)}
          placeholder="you@example.com"
          style={{ width:"100%", background:T.surfaceAlt, border:`1px solid ${T.border}`, borderRadius:4, padding:"10px 12px", fontSize:14, color:T.ink, fontFamily:"'Lato',sans-serif", outline:"none", marginBottom:16, boxSizing:"border-box" }}
        />

        <Label>Your Feedback</Label>
        <textarea
          value={comment}
          onChange={e=>setComment(e.target.value)}
          placeholder="What would you improve? Did something not work as expected during testing? Let us know…"
          rows={6}
          style={{ width:"100%", background:T.surfaceAlt, border:`1px solid ${T.border}`, borderRadius:4, padding:"10px 12px", fontSize:14, color:T.ink, fontFamily:"'Lato',sans-serif", outline:"none", resize:"vertical", boxSizing:"border-box" }}
        />
      </Card>

      <button onClick={submitFeedback} disabled={sending} style={{ width:"100%", padding:"14px", background:sending?T.borderDark:T.ink, color:"#FFF", border:"none", borderRadius:4, fontSize:14, fontWeight:700, letterSpacing:"0.04em", textTransform:"uppercase", cursor:sending?"not-allowed":"pointer", fontFamily:"'Lato',sans-serif", transition:"background 0.15s" }}>
        {sending ? "Sending…" : "📩 Send Feedback"}
      </button>

      {msg && (
        <div style={{ marginTop:12, padding:"10px 14px", borderRadius:6, fontSize:13, fontWeight:500, background:msg.type==="success"?T.greenLight:T.redLight, color:msg.type==="success"?T.green:T.red, border:`1px solid ${msg.type==="success"?T.green+"33":T.red+"33"}` }}>
          {msg.text}
        </div>
      )}
    </div>
  );
}


// ── Profile Page ─────────────────────────────────────────────────────────────
function ProfilePage({ user, getToken, profile, onProfileSaved, onOpenLocationModal }) {
  const [name,          setName]          = useState(profile.name || user?.displayName || "");
  const [dietary,       setDietary]       = useState(parseDietaryPreferences(profile.dietary));
  const [preferredChef, setPreferredChef] = useState(profile.preferredChef || "");
  const [verifiedChef,  setVerifiedChef]  = useState(profile.preferredChef ? { input:profile.preferredChef, name:profile.preferredChef } : null);
  const [chefChecking,  setChefChecking]  = useState(false);
  const [chefMsg,       setChefMsg]       = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg,    setMsg]    = useState(null);

  // Keep local fields in sync if the profile loads/changes after this page has mounted
  useEffect(() => {
    setName(profile.name || user?.displayName || "");
    setDietary(parseDietaryPreferences(profile.dietary));
    setPreferredChef(profile.preferredChef || "");
    setVerifiedChef(profile.preferredChef ? { input:profile.preferredChef, name:profile.preferredChef } : null);
    setChefMsg(null);
  }, [profile.name, profile.dietary, profile.preferredChef, user]);

  const toggleDiet = (d) => {
    setDietary(prev => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d]);
  };

  const hasLocation = hasShoppingPostalCode(profile.postalCode);
  const locationIncomplete = !!(profile.city || profile.state || profile.address) && !hasLocation;
  const locationLabel = formatProfileLocationLabel(profile);
  const chefTrimmed = preferredChef.trim();
  const chefIsVerified = !chefTrimmed || (
    verifiedChef &&
    normalizeLookupText(verifiedChef.name) === normalizeLookupText(chefTrimmed)
  );

  const handleChefChange = (value) => {
    setPreferredChef(value);
    setMsg(null);
    if (!value.trim()) {
      setVerifiedChef(null);
      setChefMsg(null);
      return;
    }
    if (verifiedChef && normalizeLookupText(value) !== normalizeLookupText(verifiedChef.name)) {
      setVerifiedChef(null);
      setChefMsg(null);
    }
  };

  const verifyChef = async () => {
    setChefChecking(true);
    setChefMsg(null);
    setMsg(null);
    try {
      const result = await verifyChefNameOnline(preferredChef, getToken);
      if (!result.ok) {
        setVerifiedChef(null);
        setChefMsg({ type:"error", text:result.message });
        return;
      }
      setPreferredChef(result.name);
      setVerifiedChef({ input:preferredChef.trim(), name:result.name, source:result.source });
      setChefMsg({ type:"success", text:`Verified as ${result.name}.` });
    } catch (e) {
      setVerifiedChef(null);
      setChefMsg({ type:"error", text:e.message || "Chef verification failed. Try again." });
    } finally {
      setChefChecking(false);
    }
  };

  const saveProfile = async () => {
    if (!chefIsVerified) {
      setMsg({ type:"error", text:"⚠ Verify the chef name or remove it before saving." });
      return;
    }
    const locationPartiallySet = !!(profile.city || profile.state || profile.address);
    if (locationPartiallySet && !hasShoppingPostalCode(profile.postalCode)) {
      setMsg({ type:"error", text:"⚠ ZIP/postal code is required for nearby store search. Update your location." });
      return;
    }
    setSaving(true); setMsg(null);
    const payload = {
      name: name.trim(),
      dietary: formatDietaryInstruction(dietary),
      preferredChef: chefTrimmed,
      address: profile.address || "",
      city: profile.city || "",
      state: profile.state || "",
      postalCode: profile.postalCode || "",
      country: profile.country || "",
    };
    try {
      const token = await getToken();
      const res = await fetch(`${BASE_URL}/profile/me`, {
        method:"PUT",
        headers:{ "Content-Type":"application/json", ...(token?{Authorization:`Bearer ${token}`}:{}) },
        body: JSON.stringify(payload),
      });
      const json = await res.json().catch(()=>({}));
      if (!res.ok) throw new Error(apiErrorMessage(json, "Failed to save profile"));
      onProfileSaved(payload);
      setMsg({ type:"success", text:"✓ Profile saved." });
    } catch (e) {
      setMsg({ type:"error", text:`⚠ ${e.message}` });
    } finally { setSaving(false); }
  };

  return (
    <div style={{ maxWidth:700, margin:"0 auto", padding:"2rem 1.5rem" }}>
      <SectionHead label="Profile" sub="Your account, location, and shopping preferences" />

      <Card style={{ marginBottom:"1.5rem" }}>
        <div style={{ display:"grid", gap:16 }}>
          <Input label="Name" value={name} onChange={e=>setName(e.target.value)} placeholder="Your name" />

          <label style={{ display:"flex", flexDirection:"column", gap:4 }}>
            <Label>Email</Label>
            <input value={user?.email || ""} disabled
              style={{ background:T.surfaceAlt, border:`1px solid ${T.border}`, borderRadius:4, padding:"10px 12px", fontSize:14, color:T.inkTer, fontFamily:"'Lato',sans-serif", width:"100%" }} />
          </label>

          <div>
            <Label>Location</Label>
            <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:12, background:T.surfaceAlt, border:`1px solid ${locationIncomplete?T.amber:T.border}`, borderRadius:4, padding:"10px 12px" }}>
              <span style={{ fontSize:14, color:(hasLocation || locationLabel)?T.ink:T.inkTer, lineHeight:1.5, flex:1, minWidth:0, wordBreak:"break-word" }}>
                {hasLocation
                  ? `📍 ${locationLabel}`
                  : locationIncomplete
                    ? (locationLabel ? `📍 ${locationLabel} — ZIP/postal code required` : "ZIP/postal code required")
                    : "Not set"}
              </span>
              <button type="button" onClick={onOpenLocationModal}
                style={{ padding:"7px 14px", borderRadius:4, border:`1px solid ${T.ink}`, background:"transparent", color:T.ink, fontSize:12, fontWeight:700, letterSpacing:"0.04em", textTransform:"uppercase", cursor:"pointer", fontFamily:"'Lato',sans-serif", whiteSpace:"nowrap", flexShrink:0 }}>
                {hasLocation ? "Update" : "Set Location"}
              </button>
            </div>
            <div style={{ marginTop:6, fontSize:11, color:locationIncomplete?T.amber:T.inkSec }}>
              {locationIncomplete
                ? "Add your ZIP/postal code so we can find nearby stores and receipt prices."
                : "Shows your full address. ZIP/postal code is used to find nearby stores and accurate prices."}
            </div>
          </div>

          <div>
            <Label>Dietary Preference</Label>
            <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
              {DIETS.map(d => (
                <button key={d} type="button" onClick={()=>toggleDiet(d)}
                  style={{ padding:"6px 12px", borderRadius:20, border:`1px solid ${dietary.includes(d)?T.green:T.border}`, background:dietary.includes(d)?T.greenLight:T.surfaceAlt, color:dietary.includes(d)?T.green:T.inkSec, fontSize:12, fontWeight:dietary.includes(d)?700:400, cursor:"pointer", fontFamily:"'Lato',sans-serif" }}>
                  {dietary.includes(d) ? "✓ " : ""}{d}
                </button>
              ))}
            </div>
            <div style={{ marginTop:6, fontSize:11, color:T.inkSec }}>
              {dietary.length ? `Combined: ${formatDietaryInstruction(dietary)}` : "No restriction — select any that apply, e.g. Vegetarian + Keto."}
            </div>
          </div>

          <div>
            <label style={{ display:"flex", flexDirection:"column", gap:4 }}>
              <Label>Preferred Chef</Label>
              <input
                value={preferredChef}
                onChange={e=>handleChefChange(e.target.value)}
                placeholder="Type a chef name"
                autoComplete="off"
                style={{ background:T.surfaceAlt, border:`1px solid ${chefIsVerified?T.border:T.amber}`, borderRadius:4, padding:"10px 12px", fontSize:14, color:T.ink, fontFamily:"'Lato',sans-serif", outline:"none", width:"100%", transition:"border-color 0.15s" }}
              />
            </label>
            <div style={{ marginTop:8, display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
              <button type="button" onClick={verifyChef} disabled={!chefTrimmed || chefChecking}
                style={{ padding:"7px 12px", borderRadius:4, border:`1px solid ${T.ink}`, background:chefIsVerified&&chefTrimmed?T.greenLight:"transparent", color:chefIsVerified&&chefTrimmed?T.green:T.ink, fontSize:12, fontWeight:700, letterSpacing:"0.04em", textTransform:"uppercase", cursor:!chefTrimmed||chefChecking?"not-allowed":"pointer", opacity:!chefTrimmed||chefChecking?0.5:1, fontFamily:"'Lato',sans-serif" }}>
                {chefChecking ? "Checking..." : chefIsVerified&&chefTrimmed ? "Verified" : "Verify Chef"}
              </button>
              {chefTrimmed && (
                <button type="button" onClick={()=>{ setPreferredChef(""); setVerifiedChef(null); setChefMsg(null); }}
                  style={{ padding:"7px 12px", borderRadius:4, border:`1px solid ${T.border}`, background:"transparent", color:T.inkSec, fontSize:12, fontWeight:700, letterSpacing:"0.04em", textTransform:"uppercase", cursor:"pointer", fontFamily:"'Lato',sans-serif" }}>
                  Remove
                </button>
              )}
            </div>
            {chefMsg && (
              <div style={{ marginTop:8, padding:"8px 10px", borderRadius:6, fontSize:12, fontWeight:600, background:chefMsg.type==="success"?T.greenLight:T.redLight, color:chefMsg.type==="success"?T.green:T.red, border:`1px solid ${chefMsg.type==="success"?T.green+"33":T.red+"33"}` }}>
                {chefMsg.text}
                {verifiedChef?.source && chefMsg.type==="success" && (
                  <a href={verifiedChef.source} target="_blank" rel="noopener noreferrer" style={{ marginLeft:6, color:T.green, textDecoration:"underline" }}>Source</a>
                )}
              </div>
            )}
            <div style={{ marginTop:6, fontSize:11, color:T.inkSec }}>Type any chef name and verify it online before saving. Unverified names are not saved.</div>
          </div>
        </div>
      </Card>

      <button onClick={saveProfile} disabled={saving} style={{ width:"100%", padding:"14px", background:saving?T.borderDark:T.ink, color:"#FFF", border:"none", borderRadius:4, fontSize:14, fontWeight:700, letterSpacing:"0.04em", textTransform:"uppercase", cursor:saving?"not-allowed":"pointer", fontFamily:"'Lato',sans-serif", transition:"background 0.15s" }}>
        {saving ? "Saving…" : "💾 Save Profile"}
      </button>

      {msg && (
        <div style={{ marginTop:12, padding:"10px 14px", borderRadius:6, fontSize:13, fontWeight:500, background:msg.type==="success"?T.greenLight:T.redLight, color:msg.type==="success"?T.green:T.red, border:`1px solid ${msg.type==="success"?T.green+"33":T.red+"33"}` }}>
          {msg.text}
        </div>
      )}
    </div>
  );
}


// ── Recipe Page ──────────────────────────────────────────────────────────────
const MAX_INGREDIENTS = 20;
const MAX_ING_LEN     = 50;
const MAX_INSTRUCTIONS = 8;
const MAX_INST_LEN     = 100;

function RecipePage({ getToken }) {
  const [recipes, setRecipes]           = useState([]);
  const [selected, setSelected]         = useState(null);
  const [ingredients, setIngredients]   = useState([]);
  const [instructions, setInstructions] = useState([]);
  const [saving, setSaving]             = useState(false);
  const [msg, setMsg]                   = useState(null);
  const [loading, setLoading]           = useState(true);

  useEffect(() => {
    getToken().then(token =>
      fetch(`${BASE_URL}/recipes/me`, {
        headers: token ? { Authorization:`Bearer ${token}` } : {}
      }).then(r=>r.json()).then(json => {
        setRecipes(json.recipes || []);
        setLoading(false);
      }).catch(()=>setLoading(false))
    );
  }, [getToken]);

  const selectRecipe = (meal) => {
    const r = recipes.find(r => r.meal === meal);
    if (!r) return;
    setSelected(r);
    setIngredients(r.ingredients || []);
    setInstructions(r.instructions || []);
    setMsg(null);
  };

  const updateIngredient = (i, val) => {
    const next = [...ingredients];
    next[i] = val.slice(0, MAX_ING_LEN);
    setIngredients(next);
  };

  const removeIngredient = (i) => setIngredients(prev => prev.filter((_,idx)=>idx!==i));
  const addIngredient = () => {
    if (ingredients.length >= MAX_INGREDIENTS) return;
    setIngredients(prev => [...prev, ""]);
  };

  const updateInstruction = (i, val) => {
    const next = [...instructions];
    next[i] = val.slice(0, MAX_INST_LEN);
    setInstructions(next);
  };

  const removeInstruction = (i) => setInstructions(prev => prev.filter((_,idx)=>idx!==i));
  const addInstruction = () => {
    if (instructions.length >= MAX_INSTRUCTIONS) return;
    setInstructions(prev => [...prev, ""]);
  };

  const ingValid  = ingredients.every(i => i.trim().length > 0 && i.length <= MAX_ING_LEN);
  const instValid = instructions.every(s => s.trim().length > 0 && s.length <= MAX_INST_LEN);
  const canSave   = selected && ingValid && instValid && ingredients.length > 0;

  const saveRecipe = async () => {
    if (!canSave) return;
    setSaving(true); setMsg(null);
    try {
      const token = await getToken();
      const res = await fetch(`${BASE_URL}/recipes`, {
        method:"PUT",
        headers:{ "Content-Type":"application/json", ...(token?{Authorization:`Bearer ${token}`}:{}) },
        body: JSON.stringify({ meal:selected.meal, dietary:selected.dietary || "vegetarian", ingredients, instructions }),
      });
      if (!res.ok) throw new Error("Save failed");
      setMsg({ type:"success", text:"✓ Recipe saved successfully" });
      // Update local list
      setRecipes(prev => prev.map(r => r.meal===selected.meal ? {...r, ingredients, instructions} : r));
    } catch(e) {
      setMsg({ type:"error", text:`⚠ ${e.message}` });
    } finally { setSaving(false); }
  };

  if (loading) return (
    <div style={{ display:"flex", justifyContent:"center", padding:"4rem" }}>
      <div style={{ width:36, height:36, border:`3px solid ${T.border}`, borderTop:`3px solid ${T.green}`, borderRadius:"50%", animation:"spin 0.8s linear infinite" }}/>
    </div>
  );

  return (
    <div style={{ maxWidth:800, margin:"0 auto", padding:"2rem 1.5rem" }}>
      <SectionHead label="My Recipes" sub="View, edit, and save your meal recipes" />

      {!recipes.length ? (
        <Card>
          <p style={{ color:T.inkSec, fontSize:14 }}>No recipes saved yet. Generate a meal plan to get started.</p>
        </Card>
      ) : (
        <>
          <Card style={{ marginBottom:"1.5rem" }}>
            <Label>Select a Recipe</Label>
            <select
              value={selected?.meal || ""}
              onChange={e=>selectRecipe(e.target.value)}
              style={{ width:"100%", background:T.surfaceAlt, border:`1px solid ${T.border}`, borderRadius:4, padding:"10px 12px", fontSize:14, color:T.ink, fontFamily:"'Lato',sans-serif", outline:"none" }}
            >
              <option value="">— Choose a recipe —</option>
              {recipes.map(r => (
                <option key={r.meal} value={r.meal} style={{ textTransform:"capitalize" }}>
                  {r.meal.split("|")[0].replace(/\b\w/g,c=>c.toUpperCase())}
                </option>
              ))}
            </select>
          </Card>

          {selected && (
            <div className="fu">
              {/* Ingredients */}
              <Card style={{ marginBottom:"1.5rem" }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"1rem" }}>
                  <div>
                    <Label>Ingredients</Label>
                    <p style={{ fontSize:11, color:T.inkSec }}>Max {MAX_INGREDIENTS} items · {MAX_ING_LEN} chars each</p>
                  </div>
                  <span style={{ fontSize:12, color:ingredients.length>=MAX_INGREDIENTS?T.red:T.inkSec, fontWeight:700 }}>{ingredients.length}/{MAX_INGREDIENTS}</span>
                </div>
                <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                  {ingredients.map((ing, i) => (
                    <div key={i} style={{ display:"flex", gap:8, alignItems:"center" }}>
                      <div style={{ flex:1, position:"relative" }}>
                        <input
                          value={ing}
                          onChange={e=>updateIngredient(i, e.target.value)}
                          placeholder={`Ingredient ${i+1}`}
                          style={{ width:"100%", background:T.surfaceAlt, border:`1px solid ${ing.length>=MAX_ING_LEN?T.red:T.border}`, borderRadius:4, padding:"8px 12px", fontSize:13, color:T.ink, fontFamily:"'Lato',sans-serif", outline:"none" }}
                        />
                        <span style={{ position:"absolute", right:8, top:"50%", transform:"translateY(-50%)", fontSize:10, color:ing.length>=MAX_ING_LEN?T.red:T.inkTer, fontFamily:"'DM Mono',monospace" }}>{ing.length}/{MAX_ING_LEN}</span>
                      </div>
                      <button onClick={()=>removeIngredient(i)} style={{ background:"none", border:"none", color:T.red, cursor:"pointer", fontSize:18, padding:"0 4px", flexShrink:0 }}>×</button>
                    </div>
                  ))}
                </div>
                {ingredients.length < MAX_INGREDIENTS && (
                  <button onClick={addIngredient} style={{ marginTop:10, fontSize:12, padding:"6px 14px", borderRadius:4, border:`1px dashed ${T.border}`, background:"transparent", color:T.inkSec, cursor:"pointer", fontFamily:"'Lato',sans-serif", width:"100%" }}>
                    + Add Ingredient
                  </button>
                )}
              </Card>

              {/* Instructions */}
              <Card style={{ marginBottom:"1.5rem" }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"1rem" }}>
                  <div>
                    <Label>Instructions</Label>
                    <p style={{ fontSize:11, color:T.inkSec }}>Max {MAX_INSTRUCTIONS} steps · {MAX_INST_LEN} chars each</p>
                  </div>
                  <span style={{ fontSize:12, color:instructions.length>=MAX_INSTRUCTIONS?T.red:T.inkSec, fontWeight:700 }}>{instructions.length}/{MAX_INSTRUCTIONS}</span>
                </div>
                <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                  {instructions.map((inst, i) => (
                    <div key={i} style={{ display:"flex", gap:8, alignItems:"flex-start" }}>
                      <span style={{ fontSize:12, fontWeight:700, color:T.inkSec, fontFamily:"'DM Mono',monospace", minWidth:24, paddingTop:10 }}>{i+1}.</span>
                      <div style={{ flex:1, position:"relative" }}>
                        <textarea
                          value={inst}
                          onChange={e=>updateInstruction(i, e.target.value)}
                          placeholder={`Step ${i+1}: Action verb + details…`}
                          rows={2}
                          style={{ width:"100%", background:T.surfaceAlt, border:`1px solid ${inst.length>=MAX_INST_LEN?T.red:T.border}`, borderRadius:4, padding:"8px 12px", fontSize:13, color:T.ink, fontFamily:"'Lato',sans-serif", outline:"none", resize:"none" }}
                        />
                        <span style={{ position:"absolute", right:8, bottom:6, fontSize:10, color:inst.length>=MAX_INST_LEN?T.red:T.inkTer, fontFamily:"'DM Mono',monospace" }}>{inst.length}/{MAX_INST_LEN}</span>
                      </div>
                      <button onClick={()=>removeInstruction(i)} style={{ background:"none", border:"none", color:T.red, cursor:"pointer", fontSize:18, padding:"0 4px", flexShrink:0, paddingTop:8 }}>×</button>
                    </div>
                  ))}
                </div>
                {instructions.length < MAX_INSTRUCTIONS && (
                  <button onClick={addInstruction} style={{ marginTop:10, fontSize:12, padding:"6px 14px", borderRadius:4, border:`1px dashed ${T.border}`, background:"transparent", color:T.inkSec, cursor:"pointer", fontFamily:"'Lato',sans-serif", width:"100%" }}>
                    + Add Step
                  </button>
                )}
              </Card>

              <button onClick={saveRecipe} disabled={!canSave||saving} style={{ width:"100%", padding:"14px", background:!canSave||saving?T.borderDark:T.ink, color:"#FFF", border:"none", borderRadius:4, fontSize:14, fontWeight:700, letterSpacing:"0.04em", textTransform:"uppercase", cursor:!canSave||saving?"not-allowed":"pointer", fontFamily:"'Lato',sans-serif", transition:"background 0.15s" }}>
                {saving ? "Saving…" : "💾 Save Recipe"}
              </button>

              {msg && (
                <div style={{ marginTop:12, padding:"10px 14px", borderRadius:6, fontSize:13, fontWeight:500, background:msg.type==="success"?T.greenLight:T.redLight, color:msg.type==="success"?T.green:T.red, border:`1px solid ${msg.type==="success"?T.green+"33":T.red+"33"}` }}>
                  {msg.text}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Login Page ───────────────────────────────────────────────────────────────
function LoginPage({ onSignIn }) {
  return (
    <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background:T.bg, padding:"2rem" }}>
      <div style={{ width:"100%", maxWidth:1200, display:"grid", gridTemplateColumns:"1fr 1fr", gap:32, alignItems:"stretch" }}>
        <div style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:24, boxShadow:"0 32px 80px rgba(0,0,0,0.08)", padding:"3rem", display:"flex", flexDirection:"column", justifyContent:"center" }}>
          <div style={{ marginBottom:28, maxWidth:520 }}>
            <div style={{ textTransform:"uppercase", letterSpacing:"0.24em", fontSize:12, fontWeight:700, color:T.greenMid, marginBottom:14 }}>SmartCart AI</div>
            <h1 style={{ fontFamily:"'Playfair Display',serif", fontSize:42, lineHeight:1.05, marginBottom:18, color:T.ink }}>Secure Google sign-in for your shopping intelligence.</h1>
            <p style={{ fontSize:16, color:T.inkSec, lineHeight:1.8 }}>Access your meal plans, pantry data, store comparisons, and AI shopping assistant with a single Google login.</p>
          </div>
          <button onClick={onSignIn} style={{ display:"inline-flex", alignItems:"center", justifyContent:"center", gap:12, width:"100%", padding:"14px 18px", borderRadius:14, border:"none", background:T.green, color:"#FFF", fontSize:15, fontWeight:700, cursor:"pointer", boxShadow:"0 14px 30px rgba(45,106,79,0.18)", transition:"transform 0.15s ease" }}>
            <GoogleIcon /> Sign in with Google
          </button>
        </div>
        <div style={{ borderRadius:24, overflow:"hidden", position:"relative", minHeight:420, display:"flex", alignItems:"center", justifyContent:"center", background:`linear-gradient(135deg, ${T.greenLight} 0%, ${T.blueLight} 100%)` }}>
          <div style={{ position:"relative", padding:"3rem", color:T.ink, textAlign:"center", maxWidth:420 }}>
            <div style={{ fontSize:72, marginBottom:18 }}>🛒</div>
            <h2 style={{ fontFamily:"'Playfair Display',serif", fontSize:34, marginBottom:16, letterSpacing:"-0.04em" }}>Welcome back.</h2>
            <p style={{ fontSize:16, color:T.inkSec, lineHeight:1.8 }}>Your personal grocery planner is ready. Sign in to continue building smarter meals and a healthier kitchen.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Receipt Page ───────────────────────────────────────────────────────────────

function ReceiptPage({ getToken }) {
  const [file,         setFile]         = useState(null);
  const [preview,      setPreview]      = useState(null);
  const [storeName,    setStoreName]    = useState("");
  const [address,      setAddress]      = useState("");
  const [city,         setCity]         = useState("");
  const [state,        setState]        = useState("");
  const [country,      setCountry]      = useState("US");
  const [postalCode,   setPostalCode]   = useState("");
  const [uploading,    setUploading]    = useState(false);
  const [result,       setResult]       = useState(null);
  const [error,        setError]        = useState(null);
  const fileRef = useRef(null);

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setResult(null);
    setError(null);

    // Preview for images only
    if (f.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (ev) => setPreview(ev.target.result);
      reader.readAsDataURL(f);
    } else {
      setPreview(null);
    }
    e.target.value = "";
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true); setResult(null); setError(null);

    try {
      // Convert file to base64
      const base64 = await new Promise((res, rej) => {
        const reader = new FileReader();
        reader.onload  = (e) => res(e.target.result.split(",")[1]);
        reader.onerror = () => rej(new Error("File read failed"));
        reader.readAsDataURL(file);
      });

      const token = await getToken();
      const res = await fetch(`${BASE_URL}/receipt/upload`, {
        method: "POST",
        headers: {
          "Content-Type":  "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          image_base64: base64,
          media_type:   file.type,
          store_name:   storeName,
          address,
          city,
          state,
          country,
          postal_code:  postalCode,
        }),
      });

      const json = await res.json().catch(() => ({}));
      if (!res.ok || json?.success === false) {
        throw new Error(json?.detail || json?.error || "Upload failed");
      }
      setResult(json);
      // Auto-fill store name if extracted from the receipt
      if (json.store_name && !storeName) setStoreName(json.store_name);
    } catch (e) {
      setError(e.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div style={{ maxWidth:700, margin:"0 auto", padding:"2rem 1.5rem" }}>
      <SectionHead label="Upload Receipt" sub="Extract real store prices from your grocery receipt" />

      {/* File Upload */}
      <Card style={{ marginBottom:"1.5rem" }}>
        <Label>Receipt Photo or PDF</Label>
        <div
          onClick={() => fileRef.current?.click()}
          style={{
            border: `2px dashed ${file ? T.green : T.border}`,
            borderRadius: 8, padding: "2rem", textAlign: "center",
            cursor: "pointer", background: file ? T.greenLight : T.surfaceAlt,
            transition: "all 0.15s", marginBottom: 12,
          }}
        >
          {preview ? (
            <img src={preview} alt="Receipt preview"
              style={{ maxHeight: 300, maxWidth: "100%", borderRadius: 4, objectFit: "contain" }} />
          ) : (
            <div>
              <div style={{ fontSize: 40, marginBottom: 8 }}>📄</div>
              <div style={{ fontSize: 14, color: T.inkSec }}>
                {file ? file.name : "Click to upload receipt photo or PDF"}
              </div>
              <div style={{ fontSize: 11, color: T.inkTer, marginTop: 4 }}>
                JPG, PNG, or PDF • Max 10MB
              </div>
            </div>
          )}
        </div>
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,application/pdf"
          onChange={handleFile} style={{ display: "none" }} />
        {file && (
          <div style={{ fontSize: 12, color: T.green, fontWeight: 700 }}>
            ✓ {file.name} ({(file.size / 1024).toFixed(0)} KB)
          </div>
        )}
      </Card>

      {/* Store Details */}
      <Card style={{ marginBottom: "1.5rem" }}>
        <div style={{ marginBottom: "1rem" }}>
          <Label>Store Details</Label>
          <p style={{ fontSize: 11, color: T.inkSec }}>
            Optional if visible on receipt — Gemini will try to extract these automatically.
          </p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div style={{ gridColumn: "1 / -1" }}>
            <Label>Country</Label>
            <select
              value={country}
              onChange={e => {
                setCountry(e.target.value);
                setState("");
                setCity("");
                setAddress("");
              }}
              style={{ width: "100%", background: T.surfaceAlt, border: `1px solid ${T.border}`, borderRadius: 4, padding: "10px 12px", fontSize: 14, boxSizing: "border-box", fontFamily: "'Lato',sans-serif" }}
            >
              <option value="US">United States</option>
              <option value="IN">India</option>
            </select>
          </div>

          <div style={{ gridColumn: "1 / -1" }}>
            <Label>State *</Label>
            <select
              value={state}
              onChange={e => { setState(e.target.value); setCity(""); setAddress(""); }}
              style={{ width: "100%", background: T.surfaceAlt, border: `1px solid ${T.border}`, borderRadius: 4, padding: "10px 12px", fontSize: 14, boxSizing: "border-box", fontFamily: "'Lato',sans-serif" }}
            >
              <option value="">Select a state…</option>
              {(STATES_BY_COUNTRY[country] || []).map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>

          <div style={{ gridColumn: "1 / -1" }}>
            <Label>City *</Label>
            <AutocompleteField
              mode="city"
              country={country}
              state={state}
              value={city}
              onChange={setCity}
              onSelect={(desc) => { setCity(desc.split(",")[0]); setAddress(""); }}
              placeholder="Start typing your city…"
              disabled={!state}
            />
          </div>

          <div style={{ gridColumn: "1 / -1" }}>
            <Label>Store Name</Label>
            <AutocompleteField
              mode="store"
              country={country}
              state={state}
              city={city}
              value={storeName}
              onChange={setStoreName}
              onSelect={(desc) => {
                const parts = desc.split(",").map(p => p.trim());
                setStoreName(parts[0] || desc);
                // Auto-fill address from the rest of the result if the user
                // hasn't already typed one — saves a second lookup.
                if (!address.trim() && parts.length > 1) {
                  setAddress(parts.slice(1, -2).join(", ") || parts[1]);
                }
              }}
              placeholder={city ? "e.g. Walmart, Whole Foods, Ratnadeep" : "Select a city first"}
              disabled={!city}
            />
          </div>
          <div style={{ gridColumn: "1 / -1" }}>
            <Input label="Address (optional)" value={address} onChange={e => setAddress(e.target.value)} placeholder="123 Main St" />
          </div>
          <Input label="ZIP / Postal Code *" value={postalCode} onChange={e => setPostalCode(e.target.value)} placeholder="e.g. 63146" required />
        </div>
        <p style={{ fontSize: 11, color: T.inkSec, marginTop: 4 }}>Adding ZIP/postal code helps pinpoint store prices more precisely than city alone.</p>
        <p style={{ fontSize: 11, color: T.red, marginTop: 8 }}>* ZIP/Postal code is required for price lookup</p>
      </Card>

      {/* Upload Button */}
      <button
        onClick={handleUpload}
        disabled={!file || uploading || !postalCode.trim()}
        style={{
          width: "100%", padding: "14px",
          background: !file || uploading ? T.borderDark : T.ink,
          color: "#FFF", border: "none", borderRadius: 4,
          fontSize: 14, fontWeight: 700, letterSpacing: "0.04em",
          textTransform: "uppercase", cursor: !file || uploading ? "not-allowed" : "pointer",
          fontFamily: "'Lato',sans-serif", transition: "background 0.15s",
          marginBottom: "1rem",
        }}
      >
        {uploading ? (
          <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
            <span style={{ width: 16, height: 16, border: "2px solid rgba(255,255,255,0.3)", borderTop: "2px solid #FFF", borderRadius: "50%", display: "inline-block", animation: "spin 0.8s linear infinite" }} />
            Analyzing Receipt…
          </span>
        ) : "📤 Upload & Extract Prices"}
      </button>

      {/* Error */}
      {error && (
        <div style={{ padding: "12px 16px", borderRadius: 6, background: T.redLight, border: `1px solid ${T.red}33`, color: T.red, fontSize: 13, marginBottom: "1rem" }}>
          ⚠ {error}
        </div>
      )}

      {/* Success Result */}
      {result && (
        <Card className="fu" style={{ borderColor: T.green }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: "1rem" }}>
            <span style={{ fontSize: 24 }}>✅</span>
            <div>
              <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 16, fontWeight: 600 }}>
                {result.store_name}
              </div>
              <div style={{ fontSize: 12, color: T.inkSec }}>
                {result.city}, {result.state} · {result.item_count} items saved
              </div>
            </div>
          </div>

          <div style={{ marginBottom: "0.75rem" }}>
            <Label>Preview (first 5 items extracted)</Label>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {Object.entries(result.items_preview || {}).map(([item, data]) => (
                <div key={item} style={{
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  padding: "8px 12px", background: T.surfaceAlt, borderRadius: 6,
                  border: `1px solid ${T.border}`, fontSize: 13,
                }}>
                  <span style={{ textTransform: "capitalize" }}>{item}</span>
                  <span style={{ fontFamily: "'DM Mono',monospace", color: T.green, fontWeight: 700 }}>
                    {formatPrice(typeof data === "object" ? (data.price ?? 0) : Number(data), typeof data === "object" ? (data.currency || "USD") : "USD")}
                    {typeof data === "object" && data.unit ? ` / ${data.unit}` : ""}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div style={{ padding: "10px 14px", borderRadius: 6, background: T.greenLight, fontSize: 12, color: T.green, fontWeight: 600 }}>
            ✓ Real prices saved to {result.city_key || result.city}. When you generate a meal plan in this city, these prices will be used instead of estimates.
            {Number(result.pantry_added_count || 0) > 0 && (
              <span> Receipt items were also added to your pantry ({result.pantry_items_count} total).</span>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}

// ── Main App ─────────────────────────────────────────────────────────────────
export default function SmartCartAI() {
  const [user,        setUser]        = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [page,        setPage]        = useState("home"); // home | pantry | recipes
  const [navOpen,     setNavOpen]     = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);

  useEffect(() => {
    return onAuthStateChanged(auth, (u) => { setUser(u); setAuthLoading(false); });
  }, []);

  const getToken = useCallback(async () => {
    if (!user) return null;
    try { return await user.getIdToken(); } catch { return null; }
  }, [user]);

  const handleSignIn  = () => signInWithPopup(auth, provider).catch(console.error);
  const handleSignOut = () => signOut(auth).catch(console.error);

  // ── Home page state ───────────────────────────────────────────────────────
  const [mode,         setMode]         = useState("mealPlanner");
  const [mealPlannerDays, setMealPlannerDays] = useState([{ breakfast:"", lunch:"", dinner:"" }]);
  const [manualText,   setManualText]   = useState("");
  const [savedRecipes, setSavedRecipes] = useState([]);
  const [activeMealSuggestion, setActiveMealSuggestion] = useState(null);
  const [pantryText,   setPantryText]   = useState("");
  const [dietary,      setDietary]      = useState("None");
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState(null);
  const [data,         setData]         = useState(null);
  const [activeTab,    setActiveTab]    = useState("list");
  const [selectedSubs, setSelectedSubs] = useState({});
  const [regenLoading, setRegenLoading] = useState(false);
  const [regenMsg,     setRegenMsg]     = useState(null);
  const [chatOpen,     setChatOpen]     = useState(false);
  const [userLatLng,   setUserLatLng]   = useState(null);
  const [weekPlan,        setWeekPlan]        = useState(null);
  const [weekPlanLoading, setWeekPlanLoading] = useState(false);
  const [weekPlanError,   setWeekPlanError]   = useState(null);
  const [pantryOpen, setPantryOpen] = useState(false);
  const [pantryList, setPantryList] = useState([]);
  const [pantryLoading, setPantryLoading] = useState(false);
  const [pantryError, setPantryError] = useState(null);
  const resultsRef     = useRef(null);
  const lastPayloadRef = useRef(null);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [manualCity, setManualCity] = useState("");
  const [manualState, setManualState] = useState("");
  const [manualPostalCode, setManualPostalCode] = useState("");
  const [manualLocationSet, setManualLocationSet] = useState(false);
  const [manualCountry, setManualCountry] = useState("US");
  const [manualAddress, setManualAddress] = useState("");

  // ── Profile (name, location, dietary preference, preferred chef) ──────────
  // Collected on the Profile page rather than during login / on the home page.
  const [preferredChef, setPreferredChef] = useState("");
  const [profileName,   setProfileName]   = useState("");

  const profile = {
    name: profileName,
    dietary,
    preferredChef,
    address: manualAddress,
    city: manualCity,
    state: manualState,
    postalCode: manualPostalCode,
    country: manualCountry,
  };

  const handleProfileSaved = (saved) => {
    setProfileName(saved.name || "");
    setDietary(saved.dietary || "None");
    setPreferredChef(saved.preferredChef || "");
    setManualAddress(saved.address || "");
    setManualCity(saved.city || "");
    setManualState(saved.state || "");
    setManualPostalCode(saved.postalCode || "");
    setManualCountry(saved.country || "US");
    setManualLocationSet(hasShoppingPostalCode(saved.postalCode));
  };

  useEffect(() => {
    // Try to silently grab coordinates for store-distance sorting; no blocking
    // prompt on login — location is collected explicitly on the Profile page.
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        pos => setUserLatLng({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => {}
      );
    }
  }, []);

  // Load saved profile (name, location, dietary preference, preferred chef) on sign-in
  useEffect(() => {
    if (authLoading || !user) return;
    getToken().then(token =>
      fetch(`${BASE_URL}/profile/me`, {
        headers: token ? { Authorization:`Bearer ${token}` } : {}
      }).then(r=>r.json()).then(json => {
        const p = json?.profile ?? json?.result ?? json ?? {};
        if (p.name) setProfileName(p.name);
        if (p.dietary) setDietary(p.dietary);
        if (p.preferredChef) setPreferredChef(p.preferredChef);
        if (p.address) setManualAddress(p.address);
        if (p.city) setManualCity(p.city);
        if (p.state) setManualState(p.state);
        if (p.postalCode) setManualPostalCode(p.postalCode);
        if (p.country) setManualCountry(p.country);
        setManualLocationSet(hasShoppingPostalCode(p.postalCode));
      }).catch(()=>{})
    );
  }, [user, authLoading, getToken]);

  // Load pantry on sign-in
  useEffect(() => {
    if (authLoading || !user) return;
    getToken().then(token =>
      fetch(`${BASE_URL}/debug/pantry/me`, {
        headers: token ? { Authorization:`Bearer ${token}` } : {}
      }).then(r=>r.json()).then(json => {
        const raw = json?.result?.items ?? [];
        const items = Array.isArray(raw)
          ? raw.map(i=>(typeof i==="string"?i:i?.name||i?.item||"")).filter(Boolean)
          : [];
        setPantryText(items.join(", "));
      }).catch(()=>{})
    );
  }, [user, authLoading, getToken]);

  // Keeps pantryText (used to build /generate's pantry_items) in sync with
  // whatever PantryPage last actually saved to Firestore. Without this,
  // pantryText stays frozen at its sign-in snapshot — so generating a plan
  // after deleting a pantry item re-sends the deleted item as part of
  // pantry_items, and the backend's add_items() merge silently resurrects it.
  const syncPantryTextFromSave = useCallback((savedItems) => {
    const names = (Array.isArray(savedItems) ? savedItems : [])
      .map(it => (typeof it === "string" ? it : it?.name))
      .filter(Boolean);
    setPantryText(names.join(", "));
  }, []);

  useEffect(() => {
    if (authLoading || !user) return;
    getToken().then(token =>
      fetch(`${BASE_URL}/recipes/me`, {
        headers: token ? { Authorization:`Bearer ${token}` } : {}
      }).then(r=>r.json()).then(json => {
        setSavedRecipes(Array.isArray(json.recipes) ? json.recipes : []);
      }).catch(()=>{})
    );
  }, [user, authLoading, getToken]);

  useEffect(() => {
    if (authLoading || !user) return;
    getToken().then(token =>
      fetch(`${BASE_URL}/recipes/me`, {
        headers: token ? { Authorization:`Bearer ${token}` } : {}
      }).then(r=>r.json()).then(json => {
        setSavedRecipes(Array.isArray(json.recipes) ? json.recipes : []);
      }).catch(()=>{})
    );
  }, [user, authLoading, getToken]);

  useEffect(() => {
    if (authLoading || !user) return;
    getToken().then(token =>
      fetch(`${BASE_URL}/recipes/me`, {
        headers: token ? { Authorization:`Bearer ${token}` } : {}
      }).then(r=>r.json()).then(json => {
        setSavedRecipes(Array.isArray(json.recipes) ? json.recipes : []);
      }).catch(()=>{})
    );
  }, [user, authLoading, getToken]);

  useEffect(() => {
    if (authLoading || !user) return;
    getToken().then(token =>
      fetch(`${BASE_URL}/recipes/me`, {
        headers: token ? { Authorization:`Bearer ${token}` } : {}
      }).then(r=>r.json()).then(json => {
        setSavedRecipes(Array.isArray(json.recipes) ? json.recipes : []);
      }).catch(()=>{})
    );
  }, [user, authLoading, getToken]);

  useEffect(() => {
    if (authLoading || !user) return;
    getToken().then(token =>
      fetch(`${BASE_URL}/recipes/me`, {
        headers: token ? { Authorization:`Bearer ${token}` } : {}
      }).then(r=>r.json()).then(json => {
        setSavedRecipes(Array.isArray(json.recipes) ? json.recipes : []);
      }).catch(()=>{})
    );
  }, [user, authLoading, getToken]);

  const resetPlanResults = () => {
    setData(null);
    setError(null);
    setWeekPlan(null);
    setWeekPlanError(null);
    setActiveTab("list");
    setSelectedSubs({});
    setRegenMsg(null);
  };

  const handleModeChange = (nextMode) => {
    if (nextMode === mode) return;
    setMode(nextMode);
    resetPlanResults();
  };

  const syncPantry = async (items) => {
    if (!user) return;
    const token = await getToken();
    const res = await fetch(`${BASE_URL}/pantry`, {
      method:"PUT",
      headers:{ "Content-Type":"application/json", ...(token?{Authorization:`Bearer ${token}`}:{}) },
      body: JSON.stringify({ items: Array.isArray(items) ? items : [] }),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok || json?.success === false) {
      throw new Error(json?.detail || json?.error || "Pantry sync failed");
    }
  };


  const openPantryModal = async () => {
    setPantryOpen(true);
    setPantryLoading(true);
    setPantryError(null);
    try {
      const token = await getToken();
      const res = await fetch(`${BASE_URL}/debug/pantry/me`, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json?.success === false) throw new Error(json?.detail || json?.error || "Could not load pantry");
      setPantryList((json.result?.items || []).map(pantryRow));
    } catch (e) {
      setPantryError(e.message || "Could not load pantry");
    } finally {
      setPantryLoading(false);
    }
  };

  const handleRegenerate = async () => {
    if (!data) return;
    setRegenLoading(true); setRegenMsg(null);
    const normalizedSubs = Object.fromEntries(
      Object.entries(selectedSubs).filter(([,v])=>v&&v!=="Keep original").map(([k,v])=>[k.toLowerCase().trim(),v])
    );
    const updatedList = (data.shopping_list||[]).map(item => {
      const itemLower = item.toLowerCase().trim();
      const matchedKey = Object.keys(normalizedSubs).find(key=>itemLower===key||itemLower.includes(key)||key.includes(itemLower));
      return matchedKey ? normalizedSubs[matchedKey] : item;
    });
    const pantryItems = pantryText.split(",").map(x=>x.trim().toLowerCase()).filter(Boolean);
    try {
      const regenPayload = {
        ...lastPayloadRef.current,
        manual_items: updatedList,
        pantry_items: pantryItems,
        selected_substitutions: Object.fromEntries(Object.entries(selectedSubs).filter(([,v])=>v&&v!=="Keep original")),
        force_refresh: true,
      };
      const token = await getToken();
      const res = await fetch(API_URL, {
        method:"POST",
        headers:{ "Content-Type":"application/json", ...(token?{Authorization:`Bearer ${token}`}:{}) },
        body: JSON.stringify(regenPayload),
      });
      if (!res.ok) throw new Error(await res.text());
      const json = await res.json();
      const savedCount = activeSubCount;
      setData(json); setSelectedSubs({}); setActiveTab("list");
      setRegenMsg(`✓ Plan regenerated! ${savedCount} substitution(s) saved.`);
      resultsRef.current?.scrollIntoView({ behavior:"smooth" });
    } catch(e) {
      setRegenMsg(`⚠ Regeneration failed: ${e.message}`);
    } finally { setRegenLoading(false); }
  };

  const activeSubCount = Object.values(selectedSubs).filter(v=>v&&v!=="Keep original").length;

  const updateMealPlannerSlot = (dayIndex, mealType, value) => {
    const key = mealType.toLowerCase();
    setMealPlannerDays(prev => prev.map((day, idx) => idx === dayIndex ? { ...day, [key]: value } : day));
  };

  const addMealPlannerDay = () => {
    setMealPlannerDays(prev => prev.length >= 7 ? prev : [...prev, { breakfast:"", lunch:"", dinner:"" }]);
  };

  const mealSuggestionsFor = (value, mealType) => {
    const q = value.trim().toLowerCase();
    if (!q) return [];
    const type = mealType.toLowerCase();
    return savedRecipes
      .filter(recipe => {
        const name = String(recipe?.meal || "").trim();
        const recipeType = String(recipe?.meal_type || recipe?.mealType || "").toLowerCase();
        return name.toLowerCase().includes(q) && (!recipeType || recipeType === type);
      })
      .slice(0, 5);
  };

  const handleShoppingListUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    const incoming = text.split(/[\n,]+/).map(item=>item.trim()).filter(Boolean).join(", ");
    setManualText(prev => [prev.trim(), incoming].filter(Boolean).join(", "));
    e.target.value = "";
  };

  const profileStateSetters = {
    setDietary,
    setPreferredChef,
    setManualAddress,
    setManualCity,
    setManualState,
    setManualPostalCode,
    setManualCountry,
    setManualLocationSet,
  };

  const ensureSavedShoppingLocation = async () => {
    try {
      const savedProfile = await loadSavedPlanningProfile(getToken);
      applySavedProfileToState(savedProfile, profileStateSetters);
      if (!hasShoppingPostalCode(savedProfile.postalCode)) {
        setError("Add your ZIP/postal code in Profile and save before generating a plan.");
        setPage("profile");
        return null;
      }
      return savedProfile;
    } catch (e) {
      setError(e.message || "Could not load saved profile.");
      return null;
    }
  };

  const handleGenerate = async () => {
    if (mode === "aiPlanner") {
      setError(null);
      await handlePlanMyWeek();
      return;
    }
    const savedProfile = await ensureSavedShoppingLocation();
    if (!savedProfile) return;
    setError(null); setData(null); setLoading(true);
    const manualItems = manualText.split(/[\n,]+/).map(x=>x.trim().toLowerCase()).filter(Boolean);
    const pantryItems = pantryText.split(",").map(x=>x.trim().toLowerCase()).filter(Boolean);
    const weekly = {};
    mealPlannerDays.forEach((day, dayIndex) => {
      MEAL_TYPES.forEach(mealType => {
        const key = mealType.toLowerCase();
        const value = String(day[key] || "").trim();
        if (value) weekly[`day_${dayIndex + 1}_${key}`] = value;
      });
    });
    if (!Object.keys(weekly).length&&!manualItems.length) {
    }
    try {
      const payload = {
        weekly_meals: weekly, manual_items: manualItems, budget:100,
        pantry_items: pantryItems, mode: "🍽️ Meal Planner",
        dietary_instruction: savedProfile.dietary || "None",
        preferred_chef: savedProfile.preferredChef || "",
        ...(userLatLng?{user_lat:userLatLng.lat,user_lng:userLatLng.lng}:{}),
        ...buildLocationPayloadFrom(savedProfile),
      };
      lastPayloadRef.current = payload;
      const token = await getToken();
      const res = await fetch(API_URL, {
        method:"POST",
        headers:{ "Content-Type":"application/json", ...(token?{Authorization:`Bearer ${token}`}:{}) },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(await res.text());
      const json = await res.json();
      await new Promise(r=>setTimeout(r,100));
      setData(json); setActiveTab("list");
      syncPantry(pantryItems).catch(e => console.warn("Pantry sync failed", e));
      setTimeout(()=>resultsRef.current?.scrollIntoView({behavior:"smooth"}),100);
    } catch(e) {
      setError(e.message||"Something went wrong.");
    } finally { setLoading(false); }
  };

  const handlePlanMyWeek = async () => {
    const savedProfile = await ensureSavedShoppingLocation();
    if (!savedProfile) return;
    setWeekPlanLoading(true); setWeekPlanError(null); setWeekPlan(null);
    try {
      const token = await getToken();
      const res = await fetch(`${BASE_URL}/plan-my-week`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({
          budget: 100,
          dietary_instruction: savedProfile.dietary || "None",
          ...(userLatLng ? { user_lat: userLatLng.lat, user_lng: userLatLng.lng } : {}),
          ...buildLocationPayloadFrom(savedProfile),
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.success) throw new Error(json.detail || json.error || "Weekly Smart Plan failed");
      setWeekPlan(json);
      setData(weekPlanToResults(json));
      setActiveTab("list");
      setTimeout(()=>resultsRef.current?.scrollIntoView({behavior:"smooth"}),100);
    } catch (e) {
      const message = e.message || "Plan My Week failed";
      setWeekPlanError(message);
      throw new Error(message);
    } finally {
      setWeekPlanLoading(false);
    }
  };

  const submitManualLocation = () => {
    if (!(manualCity.trim() && manualState.trim() && manualPostalCode.trim() && manualAddress.trim())) return;
    setManualLocationSet(true);
    setShowLocationModal(false);
  };

  const handleAddressSelect = (desc) => {
    setManualAddress(desc);
    const extracted = extractPostalCode(desc, manualCountry);
    if (extracted) setManualPostalCode(extracted);
  };

  const stores   = data?.recommended_stores ?? [];
  const route    = data?.optimized_route    ?? [];
  const list     = data?.shopping_list      ?? [];
  const subs     = data?.substitutions      ?? {};
  const loc      = data?.user_location      ?? {};
  const nutr     = data?.nutrition_report   ?? {};
  const scores   = nutr.nutrition_scores    ?? {};
  const feedback = nutr.ai_feedback         ?? {};
  const budgetOpt = data?.budget_summary?.optimization ?? {};
  const pm = stores.length ? buildMatrix(stores) : null;
  const pmExpensiveStore = pm ? Object.entries(pm.totals).sort((a,b)=>b[1]-a[1])[0]?.[0] : null;
  const pmCheapestStore  = pm ? Object.entries(pm.totals).sort((a,b)=>a[1]-b[1])[0]?.[0] : null;
  const budgetStoreTotals = budgetOpt.store_totals || {};
  const budgetExpensiveStore = budgetOpt.expensive_store || (Object.entries(budgetStoreTotals).sort((a,b)=>Number(b[1])-Number(a[1]))[0]?.[0]) || pmExpensiveStore;
  const budgetCheapestStore = budgetOpt.cheapest_store || (Object.entries(budgetStoreTotals).sort((a,b)=>Number(a[1])-Number(b[1]))[0]?.[0]) || pmCheapestStore;
  const pmOriginalCost   = Number(budgetOpt.original_cost ?? (pm&&pmExpensiveStore ? pm.totals[pmExpensiveStore] : 0)).toFixed(2);
  const pmOptimizedCost  = Number(budgetOpt.optimized_cost ?? (pm ? pm.overallCheapest : 0)).toFixed(2);
  const pmMoneySaved     = Number(budgetOpt.money_saved ?? Math.max(0,parseFloat(pmOriginalCost)-parseFloat(pmOptimizedCost))).toFixed(2);
  const pmCurrency       = budgetOpt.currency || (pm ? (Object.values(pm.currencies)[0]||"USD") : "USD");
  const weekPlanMealsByDay = DAYS.map((day, dayIndex) => ({
    day,
    meals: ["Breakfast", "Lunch", "Dinner"].map((mealType, mealTypeIndex) => {
      const normalizedMealType = mealType.toLowerCase();
      const sequentialIndex = dayIndex * 3 + mealTypeIndex;
      const meal = (weekPlan?.suggested_meals || []).find((candidate, index) => {
        const candidateDay = String(candidate?.day || "").toLowerCase();
        const candidateMealType = String(candidate?.meal_type || candidate?.mealType || "").toLowerCase();
        return (
          (candidateDay === day.toLowerCase() && candidateMealType === normalizedMealType) ||
          (!candidateDay && !candidateMealType && index === sequentialIndex)
        );
      });
      return { mealType, meal };
    }),
  }));

  if (authLoading) return (
    <><style>{css}</style>
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:"100vh", background:T.bg }}>
      <div style={{ width:36, height:36, border:`3px solid ${T.border}`, borderTop:`3px solid ${T.green}`, borderRadius:"50%", animation:"spin 0.8s linear infinite" }}/>
    </div></>
  );

  if (!user) return (<><style>{css}</style><LoginPage onSignIn={handleSignIn}/></>);

  return (
    <>
      <style>{css}</style>

      {/* Nav overlay */}
      {navOpen && <div onClick={()=>setNavOpen(false)} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.3)", zIndex:200 }}/>}

      {/* Left slide-out nav */}
      {navOpen && (
        <div className="nav-panel" style={{ position:"fixed", top:0, left:0, width:260, height:"100vh", background:T.surface, borderRight:`1px solid ${T.border}`, zIndex:300, display:"flex", flexDirection:"column", boxShadow:"4px 0 24px rgba(0,0,0,0.1)" }}>
          <div style={{ padding:"1.25rem 1.5rem", borderBottom:`1px solid ${T.border}`, display:"flex", alignItems:"center", justifyContent:"space-between", background:T.ink, color:"#FFF" }}>
            <div style={{ fontFamily:"'Playfair Display',serif", fontSize:16 }}>SmartCart <em style={{ fontWeight:400, color:T.greenMid }}>AI</em></div>
            <button onClick={()=>setNavOpen(false)} style={{ background:"transparent", border:"none", color:"#FFF", fontSize:20, cursor:"pointer", opacity:0.7 }}>✕</button>
          </div>
          <nav style={{ padding:"1rem 0", flex:1 }}>
            {[
              { id:"home",    icon:"🏠", label:"Home"    },
              { id:"pantry",  icon:"🥦", label:"Pantry"  },
              { id:"recipes", icon:"📖", label:"Recipes" },
              { id:"receipt", icon:"📄", label:"Upload Receipt" },
              { id:"stores",  icon:"⭐", label:"Preferred Stores" },
              { id:"feedback", icon:"💬", label:"Feedback" },
            ].map(item => (
              <button key={item.id} onClick={()=>{ setPage(item.id); setNavOpen(false); }}
                style={{ width:"100%", display:"flex", alignItems:"center", gap:12, padding:"14px 1.5rem", background:page===item.id?T.greenLight:"transparent", color:page===item.id?T.green:T.ink, border:"none", borderLeft:`3px solid ${page===item.id?T.green:"transparent"}`, fontSize:14, fontWeight:page===item.id?700:400, cursor:"pointer", fontFamily:"'Lato',sans-serif", textAlign:"left", transition:"all 0.15s" }}>
                <span style={{ fontSize:18 }}>{item.icon}</span>{item.label}
              </button>
            ))}
          </nav>
          {user && (
            <div style={{ padding:"1rem 1.5rem", borderTop:`1px solid ${T.border}` }}>
              <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:10 }}>
                {user.photoURL && <img src={user.photoURL} alt="" referrerPolicy="no-referrer" style={{ width:32, height:32, borderRadius:"50%" }}/>}
                <span style={{ fontSize:12, color:T.inkSec, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{user.displayName??user.email}</span>
              </div>
              <button onClick={handleSignOut} style={{ width:"100%", padding:"8px", borderRadius:4, border:`1px solid ${T.border}`, background:"transparent", color:T.inkSec, fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"'Lato',sans-serif" }}>Sign Out</button>
            </div>
          )}
        </div>
      )}

      {/* Header */}
      <header style={{ background:T.ink, color:"#FFF", padding:"1rem 2rem", display:"flex", alignItems:"center", justifyContent:"space-between", position:"sticky", top:0, zIndex:100 }}>
        <div style={{ display:"flex", alignItems:"center", gap:14 }}>
          <button onClick={()=>setNavOpen(o=>!o)} style={{ background:"transparent", border:"none", color:"#FFF", fontSize:22, cursor:"pointer", padding:"4px 6px", borderRadius:4, lineHeight:1, opacity:0.85 }} title="Menu">☰</button>
          <span style={{ fontSize:24 }}>🛒</span>
          <div>
            <div style={{ fontFamily:"'Playfair Display',serif", fontSize:20, letterSpacing:"-0.01em" }}>SmartCart <em style={{ fontWeight:400, color:T.greenMid }}>AI</em></div>
            <div style={{ fontSize:10, letterSpacing:"0.12em", opacity:0.5, textTransform:"uppercase" }}>Grocery Intelligence</div>
          </div>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
          {loc.city && page==="home" && <div style={{ fontSize:12, opacity:0.7 }}>📍 {loc.city}, {loc.region}</div>}
          {user && (
            <div style={{ position:"relative" }}>
              <button type="button" onClick={()=>setAccountOpen(o=>!o)} style={{ display:"flex", alignItems:"center", gap:8, background:"transparent", border:"1px solid rgba(255,255,255,0.18)", color:"#FFF", borderRadius:4, padding:"5px 8px", cursor:"pointer", fontFamily:"'Lato',sans-serif" }} aria-haspopup="menu" aria-expanded={accountOpen}>
                {user.photoURL && <img src={user.photoURL} alt="" referrerPolicy="no-referrer" style={{ width:28, height:28, borderRadius:"50%", border:"2px solid rgba(255,255,255,0.3)" }}/>}
                <span style={{ fontSize:12, opacity:0.85, maxWidth:140, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{user.displayName??user.email}</span>
                <span style={{ fontSize:10, opacity:0.7 }}>▾</span>
              </button>
              {accountOpen && (
                <div role="menu" style={{ position:"absolute", top:"calc(100% + 8px)", right:0, minWidth:180, background:T.surface, color:T.ink, border:`1px solid ${T.border}`, borderRadius:6, boxShadow:"0 8px 24px rgba(0,0,0,0.14)", padding:"6px", zIndex:500 }}>
                  <button type="button" role="menuitem" onClick={()=>{ setPage("profile"); setAccountOpen(false); }} style={{ width:"100%", display:"flex", alignItems:"center", gap:8, padding:"9px 10px", border:"none", borderRadius:4, background:page==="profile"?T.greenLight:"transparent", color:page==="profile"?T.green:T.ink, cursor:"pointer", fontSize:13, fontWeight:700, fontFamily:"'Lato',sans-serif", textAlign:"left" }}>
                    <span>👤</span>
                    Profile
                  </button>
                </div>
              )}
            </div>
          )}
          {page==="home" && (
            <button onClick={()=>setChatOpen(o=>!o)} style={{ ...headerBtnStyle, background:chatOpen?T.greenMid:"rgba(255,255,255,0.1)", border:`1px solid ${chatOpen?T.greenMid:"rgba(255,255,255,0.2)"}` }}>
              <span style={{ fontSize:16 }}>🤖</span> AI Agent
            </button>
          )}
        </div>
      </header>

      {/* Page routing */}
      {page === "pantry"  && <PantryPage  getToken={getToken} onSaved={syncPantryTextFromSave} />}
      {page === "recipes" && <RecipePage  getToken={getToken} />}
      {page === "receipt" && <ReceiptPage getToken={getToken} />}
      {page === "stores"  && <StoresPage  user={user} />}
      {page === "profile" && <ProfilePage user={user} getToken={getToken} profile={profile} onProfileSaved={handleProfileSaved} onOpenLocationModal={()=>setShowLocationModal(true)} />}
      {page === "feedback" && <FeedbackPage getToken={getToken} />}

      {/* Home page */}
      {page === "home" && (
        <div style={{ marginRight:chatOpen?380:0, transition:"margin-right 0.25s ease", maxWidth:chatOpen?"none":1080, margin:chatOpen?"0":"0 auto", padding:"2rem 1.5rem" }}>
          <div style={{ maxWidth:chatOpen?800:"none", margin:"0 auto" }}>

            <Card className="fu" style={{ marginBottom:"2rem" }}>
              <SectionHead label="Plan Your Groceries" sub="AI builds your optimized shopping plan" />
              <div style={{ display:"flex", gap:8, marginBottom:"1.5rem", flexWrap:"wrap" }}>
                <ModeBtn active={mode==="mealPlanner"} onClick={()=>handleModeChange("mealPlanner")}>🍽️ Meal Planner</ModeBtn>
                <ModeBtn active={mode==="aiPlanner"} onClick={()=>handleModeChange("aiPlanner")}>🤖 AI Planner</ModeBtn>
              </div>
              {mode==="mealPlanner" && (
                <div style={{ display:"grid", gap:"1rem", marginBottom:"1.25rem" }}>
                  <div style={{ display:"grid", gap:12 }}>
                    {mealPlannerDays.map((day, dayIndex) => (
                      <div key={`meal-planner-day-${dayIndex}`} style={{ padding:"14px", border:`1px solid ${T.border}`, borderRadius:8, background:T.surfaceAlt }}>
                        <div style={{ fontSize:12, fontWeight:900, color:T.blue, letterSpacing:"0.05em", textTransform:"uppercase", marginBottom:10 }}>Day {dayIndex + 1}</div>
                        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(190px,1fr))", gap:10 }}>
                          {MEAL_TYPES.map(mealType => {
                            const key = mealType.toLowerCase();
                            const inputId = `${dayIndex}-${key}`;
                            const suggestions = mealSuggestionsFor(day[key] || "", mealType);
                            return (
                              <div key={mealType} style={{ position:"relative" }}>
                                <Label>{mealType}</Label>
                                <input
                                  value={day[key] || ""}
                                  onChange={e=>updateMealPlannerSlot(dayIndex, mealType, e.target.value)}
                                  onFocus={()=>setActiveMealSuggestion(inputId)}
                                  onBlur={()=>setTimeout(()=>setActiveMealSuggestion(null),150)}
                                  placeholder={`Type or choose ${key}...`}
                                  style={{ width:"100%", padding:"12px 14px", border:`1px solid ${T.borderDark}`, borderRadius:4, background:T.surface, color:T.ink, fontSize:14, fontFamily:"'Lato',sans-serif", outline:"none" }}
                                />
                                {activeMealSuggestion===inputId && !!suggestions.length && (
                                  <div style={{ position:"absolute", top:"calc(100% + 4px)", left:0, right:0, zIndex:50, background:T.surface, border:`1px solid ${T.border}`, borderRadius:6, boxShadow:"0 10px 24px rgba(0,0,0,0.12)", overflow:"hidden" }}>
                                    {suggestions.map((recipe, idx) => (
                                      <button
                                        key={`${inputId}-${recipe.meal}-${idx}`}
                                        type="button"
                                        onMouseDown={(e)=>{ e.preventDefault(); updateMealPlannerSlot(dayIndex, mealType, mealNameOnly(recipe.meal)); setActiveMealSuggestion(null); }}
                                        style={{ width:"100%", display:"flex", justifyContent:"space-between", gap:10, padding:"9px 11px", border:"none", borderBottom:idx===suggestions.length-1?"none":`1px solid ${T.border}`, background:T.surface, color:T.ink, cursor:"pointer", fontFamily:"'Lato',sans-serif", textAlign:"left" }}
                                      >
                                        <span style={{ fontSize:13, fontWeight:800 }}>{mealNameOnly(recipe.meal)}</span>
                                        <span style={{ fontSize:10, color:T.inkSec, textTransform:"uppercase", letterSpacing:"0.04em" }}>{recipe.meal_type || "saved"}</span>
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={addMealPlannerDay}
                    disabled={mealPlannerDays.length >= 7}
                    style={{ justifySelf:"start", padding:"9px 14px", border:`1px solid ${T.green}`, background:mealPlannerDays.length>=7?T.surfaceAlt:T.greenLight, color:mealPlannerDays.length>=7?T.inkSec:T.green, borderRadius:4, fontSize:12, fontWeight:900, letterSpacing:"0.04em", textTransform:"uppercase", cursor:mealPlannerDays.length>=7?"not-allowed":"pointer", fontFamily:"'Lato',sans-serif" }}
                  >
                    {mealPlannerDays.length >= 7 ? "7 Day Limit Reached" : "+ Add Day"}
                  </button>
                  <div style={{ display:"grid", gridTemplateColumns:"minmax(0,1fr) minmax(220px,0.45fr)", gap:"1rem", alignItems:"end" }}>
                    <Textarea label="Shopping list items — optional (comma or line separated)" value={manualText} onChange={e=>setManualText(e.target.value)} placeholder="tomatoes, olive oil, pasta..." rows={3}/>
                    <div>
                      <Label>Upload shopping list — optional</Label>
                      <input type="file" accept=".txt,.csv,text/plain,text/csv" onChange={handleShoppingListUpload} style={{ width:"100%", padding:"10px", border:`1px solid ${T.borderDark}`, borderRadius:4, background:T.surface, color:T.ink, fontSize:12, fontFamily:"'Lato',sans-serif" }}/>
                      <div style={{ marginTop:6, fontSize:11, color:T.inkSec }}>Text or CSV uploads append items to the shopping list above.</div>
                    </div>
                  </div>
                </div>
              )}
              <button onClick={handleGenerate} disabled={mode==="aiPlanner" ? weekPlanLoading : loading} style={{ width:"100%", padding:"14px", background:(mode==="aiPlanner" ? weekPlanLoading : loading)?T.borderDark:T.ink, color:"#FFF", border:"none", borderRadius:4, fontSize:14, fontWeight:700, letterSpacing:"0.04em", textTransform:"uppercase", cursor:(mode==="aiPlanner" ? weekPlanLoading : loading)?"not-allowed":"pointer", fontFamily:"'Lato',sans-serif", transition:"background 0.15s", opacity:(mode==="aiPlanner" ? weekPlanLoading : loading)?0.7:1 }}>
                {(mode==="aiPlanner" ? weekPlanLoading : loading)?"Generating Plan…":"✦ Generate Smart Plan"}
              </button>
              {error && <div style={{ marginTop:"1rem", padding:"12px 16px", borderRadius:6, background:T.redLight, border:"1px solid #f5c6c3", color:T.red, fontSize:13 }}>⚠ {error}</div>}
            </Card>

            {(weekPlan || weekPlanError || weekPlanLoading) && (
            <Card className="fu" style={{ marginBottom:"2rem", borderColor:weekPlanError?T.red+"55":T.blue+"33" }}>
              <div style={{ display:"flex", justifyContent:"space-between", gap:16, alignItems:"center", flexWrap:"wrap" }}>
                <div>
                  <div style={{ fontFamily:"'Playfair Display',serif", fontSize:22, color:T.ink }}>Weekly Smart Plan</div>
                  <div style={{ fontSize:13, color:T.inkSec, marginTop:3 }}>One agent action chains pantry, meals, shopping list, budget, and receipt-aware store prices.</div>
                </div>
              </div>
              {weekPlanError && <div style={{ marginTop:"1rem", padding:"10px 14px", borderRadius:6, background:T.redLight, color:T.red, border:`1px solid ${T.red}33`, fontSize:13 }}>⚠ {weekPlanError}</div>}
              {weekPlan && (
                <div style={{ marginTop:"1.25rem", display:"grid", gap:12 }}>
                  <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))", gap:10 }}>
                    {weekPlanMealsByDay.map(({ day, meals }) => (
                      <div key={day} style={{ padding:"12px", border:`1px solid ${T.border}`, borderRadius:6, background:T.surfaceAlt }}>
                        <div style={{ fontSize:12, fontWeight:900, color:T.blue, letterSpacing:"0.05em", textTransform:"uppercase", marginBottom:8 }}>{day}</div>
                        <div style={{ display:"grid", gap:8 }}>
                          {meals.map(({ mealType, meal }) => (
                            <div key={`${day}-${mealType}`} style={{ padding:"8px 10px", border:`1px solid ${T.border}`, borderRadius:5, background:T.surface }}>
                              <div style={{ fontSize:10, fontWeight:900, color:T.inkSec, letterSpacing:"0.05em", textTransform:"uppercase" }}>{mealType}</div>
                              <div style={{ fontSize:13, fontWeight:800, color:T.ink, marginTop:2 }}>{meal?.name || "Meal pending"}</div>
                              {meal?.reason && <div style={{ fontSize:11, color:T.inkSec, marginTop:3 }}>{meal.reason}</div>}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card>
            )}

            {loading && (
              <Card style={{ textAlign:"center", padding:"3rem" }}>
                <div style={{ width:40, height:40, margin:"0 auto 1rem", border:`3px solid ${T.border}`, borderTop:`3px solid ${T.green}`, borderRadius:"50%", animation:"spin 0.8s linear infinite" }}/>
                <p style={{ color:T.inkSec, fontFamily:"'Playfair Display',serif", fontStyle:"italic" }}>Building your grocery plan…</p>
              </Card>
            )}

            {data && !loading && (
              <div ref={resultsRef}>
                <div style={{ display:"flex", gap:4, marginBottom:"1.25rem", overflowX:"auto", paddingBottom:2 }}>
                  {TABS.map(t=><TabBtn key={t.id} active={activeTab===t.id} onClick={()=>setActiveTab(t.id)}>{t.label}</TabBtn>)}
                </div>


                {activeTab==="list" && (
                  <Card className="fu1">
                    <SectionHead label="Compiled Shopping List" sub={`${list.length} items`} delay="1"/>
                    {!list.length ? <p style={{ color:T.inkSec }}>No items generated.</p> : (
                      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))", gap:8 }}>
                        {list.map((item,i)=>(
                          <div key={i} style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 14px", borderRadius:6, background:T.surfaceAlt, border:`1px solid ${T.border}` }}>
                            <span style={{ color:T.green, fontWeight:700 }}>✓</span>
                            <span style={{ fontSize:13, textTransform:"capitalize" }}>{item}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </Card>
                )}

                {activeTab==="stores" && (
                  <div className="fu1">
                    <PreferredStoresPanel userId={user?.uid} country={loc.country || manualCountry || "US"} />
                    {!stores.length ? <Card><p style={{ color:T.inkSec }}>No stores found.</p></Card> : pm && (
                      <>
                        {(()=>{
                          const sortedStores=[...stores].sort((a,b)=>(pm.totals[a.store_name]||Infinity)-(pm.totals[b.store_name]||Infinity));
                          const cheapestTotal=pm.totals[sortedStores[0]?.store_name]||0;
                          return (
                            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))", gap:12, marginBottom:"1.25rem" }}>
                              {sortedStores.map((store,i)=>{
                                const name=store.store_name, total=pm.totals[name]||0, isCheapest=i===0;
                                const currency=pm.currencies[name]||"USD";
                                const itemsWon=pm.wCounts[name]||0, rank=i===0?"🥇 Cheapest Basket":i===1?"🥈 Runner Up":"🥉 Option 3";
                                const savings=total-cheapestTotal;
                                return (
                                  <Card key={name} style={{ borderColor:isCheapest?T.green:T.border }}>
                                    <div style={{ display:"flex", gap:6, marginBottom:8 }}>
                                      <span style={{ display:"inline-block", fontSize:11, fontWeight:700, letterSpacing:"0.06em", textTransform:"uppercase", padding:"2px 8px", borderRadius:3, background:isCheapest?T.greenLight:T.surfaceAlt, color:isCheapest?T.green:T.inkSec }}>{rank}</span>
                                      {store.is_preferred && <span style={{ display:"inline-flex", alignItems:"center", gap:3, fontSize:11, fontWeight:700, padding:"2px 8px", borderRadius:3, background:T.amberLight, color:T.amber }}><span>★</span>Your Store</span>}
                                    </div>
                                    <div style={{ fontFamily:"'Playfair Display',serif", fontSize:16, marginBottom:4 }}>{name}</div>
                                    <div style={{ fontFamily:"'DM Mono',monospace", fontSize:22, fontWeight:500, color:isCheapest?T.green:T.ink, marginBottom:2 }}>{formatPrice(total,currency)}</div>
                                    {!isCheapest&&savings>0&&<div style={{ fontSize:12, color:T.red, marginBottom:4 }}>+{formatPrice(savings,currency)} vs cheapest</div>}
                                    <span style={{ fontSize:12, color:T.inkSec }}>{itemsWon>0?`${itemsWon} cheapest items`:"No cheapest items"}</span>
                                  </Card>
                                );
                              })}
                            </div>
                          );
                        })()}
                        {(()=>{
                          const minStoreTotal=Math.min(...stores.map(s=>pm.totals[s.store_name]||Infinity));
                          const savings=minStoreTotal-pm.overallCheapest;
                          const currency=Object.values(pm.currencies)[0]||"USD";
                          return (
                            <div style={{ background:T.greenLight, border:`1px solid ${T.green}33`, borderRadius:8, padding:"1rem 1.25rem", marginBottom:"1.25rem", display:"flex", alignItems:"center", gap:12 }}>
                              <span style={{ fontSize:22 }}>🏆</span>
                              <div>
                                <div style={{ fontSize:11, fontWeight:700, color:T.green, letterSpacing:"0.06em", textTransform:"uppercase" }}>Cheapest Possible Basket (Multi-Store)</div>
                                <div style={{ fontFamily:"'DM Mono',monospace", fontSize:24, fontWeight:500, color:T.green }}>{formatPrice(pm.overallCheapest,currency)}</div>
                                <div style={{ fontSize:12, color:T.green }}>{savings>0.01?`Saves ${formatPrice(savings,currency)} vs single store`:"Best value across stores"}</div>
                              </div>
                            </div>
                          );
                        })()}
                        <Card>
                          <SectionHead label="Estimated Price Comparison Table" sub="Green = cheapest for that item" delay="1"/>
                          <div style={{ overflowX:"auto" }}>
                            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
                              <thead>
                                <tr>
                                  <th style={{ textAlign:"left", padding:"8px 12px", borderBottom:`2px solid ${T.border}`, fontSize:11, fontWeight:700, letterSpacing:"0.04em", textTransform:"uppercase", color:T.inkSec }}>Item</th>
                                  {stores.map(s=><th key={s.store_name} style={{ textAlign:"right", padding:"8px 12px", borderBottom:`2px solid ${T.border}`, fontSize:11, fontWeight:700, letterSpacing:"0.04em", textTransform:"uppercase", color:T.inkSec, whiteSpace:"nowrap" }}>{s.store_name}</th>)}
                                </tr>
                              </thead>
                              <tbody>
                                {pm.items.map((item,ri)=>{
                                  const rowPrices=stores.map(s=>pm.matrix[s.store_name]?.[item]);
                                  const minP=Math.min(...rowPrices.filter(p=>p!==undefined));
                                  return (
                                            <tr key={item} style={{ background:ri%2===0?T.surfaceAlt:T.surface }}>
                                              <td style={{ padding:"8px 12px", textTransform:"capitalize", fontWeight:500 }}>{item}</td>
                                              {stores.map(s=>{
                                                const p=pm.matrix[s.store_name]?.[item], isMin=p===minP, curr=pm.currencies[s.store_name]||"USD";
                                                return (
                                                  <td key={s.store_name} style={{ padding:"8px 12px", textAlign:"right", fontFamily:"'DM Mono',monospace", background:isMin?T.greenLight:p===undefined?"#fff5f5":"transparent", color:isMin?T.green:p===undefined?T.red:T.ink, fontWeight:isMin?700:400 }}>
                                                    {p!==undefined ? formatPrice(p,curr) : <span title="Not available">N/A</span>}
                                                  </td>
                                                );
                                              })}
                                            </tr>
                                          );
                                })}
                                <tr style={{ borderTop:`2px solid ${T.border}` }}>
                                  <td style={{ padding:"10px 12px", fontWeight:700, fontFamily:"'Playfair Display',serif" }}>🛒 Total Basket</td>
                                  {(()=>{ const minTotal=Math.min(...stores.map(st=>pm.totals[st.store_name]||Infinity)); return stores.map(s=>{ const total=pm.totals[s.store_name]||0, isMin=Math.abs(total-minTotal)<0.001, curr=pm.currencies[s.store_name]||"USD"; return <td key={s.store_name} style={{ padding:"10px 12px", textAlign:"right", fontFamily:"'DM Mono',monospace", fontWeight:700, background:isMin?T.greenLight:"transparent", color:isMin?T.green:T.ink }}>{formatPrice(total,curr)}{isMin&&<span style={{ fontSize:10, marginLeft:4 }}>✓ CHEAPEST</span>}</td>; }); })()}
                                </tr>
                              </tbody>
                            </table>
                            <p style={{ fontSize:11, color:T.inkSec, marginTop:8, fontStyle:"italic" }}>* <span style={{ color:T.red }}>N/A</span> = item not carried at this store. 🧾 Receipt = verified uploaded receipt price. 🌐 = live online price for that specific store. Unlabeled = general market estimate, not store-specific.</p>
                          </div>
                        </Card>
                      </>
                    )}
                  </div>
                )}

                {activeTab==="route" && (
                  <Card className="fu1">
                    <SectionHead label="Optimized Shopping Route" sub="Nearest-neighbor route optimization" delay="1"/>
                    {!route.length ? <p style={{ color:T.inkSec }}>No route available.</p> : (
                      <>
                        <div style={{ marginBottom:"1.5rem" }}>
                          {[{stop:0,store_name:`📍 ${loc.city||"Your Location"}`,isStart:true},...route].map((stop,i)=>(
                            <div key={i} style={{ display:"flex", gap:16, alignItems:"flex-start" }}>
                              <div style={{ display:"flex", flexDirection:"column", alignItems:"center" }}>
                                <div style={{ width:32, height:32, borderRadius:"50%", background:stop.isStart?T.greenLight:T.surfaceAlt, border:`2px solid ${stop.isStart?T.green:T.border}`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, fontFamily:"'DM Mono',monospace", color:stop.isStart?T.green:T.inkSec, flexShrink:0 }}>{stop.isStart?"★":stop.stop}</div>
                                {i<route.length&&<div style={{ width:2, height:28, background:T.border }}/>}
                              </div>
                              <div style={{ paddingBottom:12, paddingTop:5 }}>
                                <div style={{ fontSize:15, fontWeight:600, fontFamily:"'Playfair Display',serif", color:stop.isStart?T.green:T.ink }}>{stop.store_name}</div>
                                {!stop.isStart&&Number.isFinite(Number(stop.distance_km))&&Number(stop.distance_km)<999&&<div style={{ fontSize:12, color:T.inkSec, marginTop:2 }}>{stop.distance_km} km away</div>}
                                {!stop.isStart&&stop.address&&<div style={{ fontSize:12, color:T.inkSec, marginTop:2 }}>{stop.address}</div>}
                                {!stop.isStart&&stop.rating&&<div style={{ fontSize:11, color:T.amber, marginTop:2 }}>★ {stop.rating}</div>}
                              </div>
                            </div>
                          ))}
                        </div>
                        {loc.lat&&route.length>0&&(
                          <a href={`https://www.google.com/maps/dir/${loc.lat},${loc.lng}/${route.map(s=>`${s.lat},${s.lng}`).join("/")}`} target="_blank" rel="noopener noreferrer" style={{ display:"inline-block", padding:"10px 20px", background:T.ink, color:"#FFF", borderRadius:4, fontSize:12, fontWeight:700, letterSpacing:"0.04em", textTransform:"uppercase", textDecoration:"none", fontFamily:"'Lato',sans-serif" }}>
                            Open in Google Maps ↗
                          </a>
                        )}
                      </>
                    )}
                  </Card>
                )}

                {activeTab==="nutrition" && (
                  <div className="fu1" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"1.25rem" }}>
                    <Card>
                      <SectionHead label="Nutrition Report" delay="1"/>
                      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:8, marginBottom:"1.25rem" }}>
                        {[
                          {label:"Calories",value:scores.calories??0,unit:"kcal",color:T.amber},
                          {label:"Protein",value:scores.protein_g??0,unit:"g",color:T.green},
                          {label:"Carbs",value:scores.carbs_g??0,unit:"g",color:T.blue},
                          {label:"Fat",value:scores.fat_g??0,unit:"g",color:T.red},
                          {label:"Fiber",value:scores.fiber_g??0,unit:"g",color:T.greenMid},
                        ].map(m=>(
                          <div key={m.label} style={{ padding:"10px 8px", background:T.surfaceAlt, borderRadius:6, textAlign:"center", border:`1px solid ${T.border}` }}>
                            <div style={{ fontFamily:"'DM Mono',monospace", fontSize:18, fontWeight:500, color:m.color }}>{m.value}</div>
                            <div style={{ fontSize:10, color:T.inkSec, fontWeight:700, letterSpacing:"0.04em", textTransform:"uppercase" }}>{m.unit}</div>
                            <div style={{ fontSize:11, color:T.inkSec, marginTop:2 }}>{m.label}</div>
                          </div>
                        ))}
                      </div>
                      <ScoreBar label="Protein Score"   value={scores.protein_score??0}   color={T.green}/>
                      <ScoreBar label="Vegetable Score" value={scores.vegetable_score??0} color={T.greenMid}/>
                      <ScoreBar label="Carb Score"      value={scores.carb_score??0}      color={T.amber}/>
                      <ScoreBar label="Fat Score"       value={scores.fat_score??0}       color={T.blue}/>
                      <div style={{ marginTop:"1rem", padding:"12px 16px", background:T.surfaceAlt, borderRadius:6, textAlign:"center" }}>
                        <div style={{ fontSize:11, fontWeight:700, color:T.inkSec, letterSpacing:"0.06em", textTransform:"uppercase", marginBottom:4 }}>Health Rating</div>
                        <div style={{ fontFamily:"'Playfair Display',serif", fontSize:20, color:T.green }}>{scores.health_rating||"—"}</div>
                      </div>
                    </Card>
                    <Card>
                      <SectionHead label="AI Feedback" delay="2"/>
                      {feedback.summary&&<p style={{ fontSize:13, color:T.inkSec, lineHeight:1.7, marginBottom:"1rem" }}>{feedback.summary}</p>}
                      {feedback.strengths?.length>0&&(
                        <div style={{ marginBottom:"1rem" }}>
                          <div style={{ fontSize:11, fontWeight:700, color:T.green, letterSpacing:"0.06em", textTransform:"uppercase", marginBottom:6 }}>Strengths</div>
                          {feedback.strengths.map((s,i)=><div key={i} style={{ fontSize:13, padding:"5px 0", borderBottom:`1px solid ${T.border}`, display:"flex", gap:8 }}><span style={{ color:T.green }}>+</span>{s}</div>)}
                        </div>
                      )}
                      {feedback.weaknesses?.length>0&&(
                        <div style={{ marginBottom:"1rem" }}>
                          <div style={{ fontSize:11, fontWeight:700, color:T.red, letterSpacing:"0.06em", textTransform:"uppercase", marginBottom:6 }}>Weaknesses</div>
                          {feedback.weaknesses.map((w,i)=><div key={i} style={{ fontSize:13, padding:"5px 0", borderBottom:`1px solid ${T.border}`, display:"flex", gap:8 }}><span style={{ color:T.red }}>−</span>{w}</div>)}
                        </div>
                      )}
                      {feedback.recommendations?.length>0&&(
                        <div>
                          <div style={{ fontSize:11, fontWeight:700, color:T.blue, letterSpacing:"0.06em", textTransform:"uppercase", marginBottom:6 }}>Recommendations</div>
                          {feedback.recommendations.map((r,i)=><div key={i} style={{ fontSize:13, padding:"5px 0", borderBottom:`1px solid ${T.border}`, display:"flex", gap:8 }}><span style={{ color:T.blue }}>→</span>{r}</div>)}
                        </div>
                      )}
                    </Card>
                  </div>
                )}

                {activeTab==="subs" && (
                  <Card className="fu1">
                    <SectionHead label="Smart Substitutions" sub="Choose your preferred alternative, then regenerate" delay="1"/>
                    {!Object.keys(subs).length ? <p style={{ color:T.inkSec }}>No substitutions generated.</p> : (
                      <>
                        <div style={{ display:"flex", flexDirection:"column", gap:16, marginBottom:"1.5rem" }}>
                          {Object.entries(subs).map(([item,options])=>(
                            <div key={item} style={{ padding:"1rem 1.25rem", borderRadius:6, background:T.surfaceAlt, border:`1px solid ${T.border}` }}>
                              <div style={{ fontFamily:"'Playfair Display',serif", fontWeight:600, textTransform:"capitalize", marginBottom:10 }}>{item}</div>
                              <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                                {["Keep original",...(Array.isArray(options)?options:[])].map(opt=>(
                                  <button key={opt} onClick={()=>setSelectedSubs(p=>({...p,[item]:opt}))}
                                    style={{ fontSize:12, padding:"5px 12px", borderRadius:20, border:`1px solid ${selectedSubs[item]===opt||(!selectedSubs[item]&&opt==="Keep original")?T.green:T.border}`, background:selectedSubs[item]===opt||(!selectedSubs[item]&&opt==="Keep original")?T.greenLight:"transparent", color:selectedSubs[item]===opt||(!selectedSubs[item]&&opt==="Keep original")?T.green:T.inkSec, cursor:"pointer", fontFamily:"'Lato',sans-serif", fontWeight:700, textTransform:"capitalize", transition:"all 0.15s" }}>
                                    {opt==="Keep original"?"✓ Keep original":`↳ ${opt}`}
                                  </button>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                        <div style={{ borderTop:`1px solid ${T.border}`, paddingTop:"1.25rem" }}>
                          {activeSubCount>0&&<div style={{ marginBottom:10, fontSize:13, color:T.green, fontWeight:600 }}>✓ {activeSubCount} substitution{activeSubCount>1?"s":""} selected</div>}
                          <button onClick={handleRegenerate} disabled={regenLoading} style={{ padding:"12px 28px", background:activeSubCount>0?T.green:T.borderDark, color:"#FFF", border:"none", borderRadius:4, fontSize:13, fontWeight:700, letterSpacing:"0.04em", textTransform:"uppercase", cursor:activeSubCount>0?"pointer":"not-allowed", fontFamily:"'Lato',sans-serif", transition:"background 0.15s", opacity:regenLoading?0.7:1 }}>
                            {regenLoading?"Regenerating…":"↺ Regenerate Plan with Substitutions"}
                          </button>
                          <p style={{ fontSize:12, color:T.inkSec, marginTop:8 }}>{activeSubCount===0?"Select at least one substitution above to regenerate":"This will rebuild your shopping list with the new items"}</p>
                          {regenMsg&&<div style={{ marginTop:10, padding:"10px 14px", borderRadius:6, fontSize:13, fontWeight:500, background:regenMsg.startsWith("✓")?T.greenLight:T.redLight, color:regenMsg.startsWith("✓")?T.green:T.red, border:`1px solid ${regenMsg.startsWith("✓")?T.green+"33":T.red+"33"}` }}>{regenMsg}</div>}
                        </div>
                      </>
                    )}
                  </Card>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Floating chat button */}
      {page==="home" && !chatOpen && (
        <button onClick={()=>setChatOpen(true)} style={{ position:"fixed", bottom:28, right:28, zIndex:999, width:60, height:60, borderRadius:"50%", background:T.green, border:"none", color:"#FFF", fontSize:24, cursor:"pointer", boxShadow:"0 4px 20px rgba(45,106,79,0.4)", display:"flex", alignItems:"center", justifyContent:"center", transition:"transform 0.15s, box-shadow 0.15s" }}
          onMouseEnter={e=>{e.currentTarget.style.transform="scale(1.1)";e.currentTarget.style.boxShadow="0 6px 28px rgba(45,106,79,0.5)";}}
          onMouseLeave={e=>{e.currentTarget.style.transform="scale(1)";e.currentTarget.style.boxShadow="0 4px 20px rgba(45,106,79,0.4)";}}>
          🤖
        </button>
      )}

      {page==="home" && chatOpen && <ChatPanel onClose={()=>setChatOpen(false)} user={user} getToken={getToken}/>}


      {pantryOpen && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000 }}>
          <div style={{ background:T.surface, borderRadius:8, padding:"1.5rem", width:"min(720px,92vw)", maxHeight:"80vh", overflow:"auto", boxShadow:"0 8px 32px rgba(0,0,0,0.2)" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", gap:12, marginBottom:"1rem" }}>
              <div>
                <h3 style={{ margin:0, fontSize:20, fontFamily:"'Playfair Display',serif" }}>Pantry Items</h3>
                <p style={{ margin:"4px 0 0", fontSize:12, color:T.inkSec }}>Items saved from receipts and pantry updates.</p>
              </div>
              <button type="button" onClick={()=>setPantryOpen(false)} style={{ border:`1px solid ${T.border}`, background:T.surfaceAlt, color:T.ink, borderRadius:4, padding:"6px 10px", cursor:"pointer", fontWeight:800 }}>✕</button>
            </div>
            {pantryLoading ? (
              <p style={{ color:T.inkSec }}>Loading pantry…</p>
            ) : pantryError ? (
              <div style={{ padding:"10px 12px", borderRadius:6, background:T.redLight, color:T.red, border:`1px solid ${T.red}33`, fontSize:13 }}>⚠ {pantryError}</div>
            ) : (
              <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
                <thead>
                  <tr>
                    <th style={{ textAlign:"left", padding:"8px 10px", borderBottom:`2px solid ${T.border}` }}>Item Name</th>
                    <th style={{ textAlign:"left", padding:"8px 10px", borderBottom:`2px solid ${T.border}` }}>Qty</th>
                    <th style={{ textAlign:"left", padding:"8px 10px", borderBottom:`2px solid ${T.border}` }}>Weight</th>
                  </tr>
                </thead>
                <tbody>
                  {pantryList.length ? pantryList.map((item,i)=>(
                    <tr key={`${item.name}-${i}`} style={{ background:i%2===0?T.surfaceAlt:T.surface }}>
                      <td style={{ padding:"8px 10px", textTransform:"capitalize", borderBottom:`1px solid ${T.border}` }}>{item.name}</td>
                      <td style={{ padding:"8px 10px", borderBottom:`1px solid ${T.border}` }}>{item.qty || "—"}</td>
                      <td style={{ padding:"8px 10px", borderBottom:`1px solid ${T.border}` }}>{item.weight || "—"}</td>
                    </tr>
                  )) : (
                    <tr><td colSpan="3" style={{ padding:"14px 10px", color:T.inkSec, textAlign:"center" }}>No pantry items saved yet.</td></tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {showLocationModal && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
          display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000
        }}>
          <div style={{
            background: T.surface, borderRadius: 8, padding: "2rem",
            maxWidth: 400, width: "90%", maxHeight: "85vh", overflowY: "auto",
            boxShadow: "0 8px 32px rgba(0,0,0,0.2)", position: "relative"
          }}>
            <button type="button" onClick={()=>setShowLocationModal(false)} style={{ position:"absolute", top:14, right:14, border:`1px solid ${T.border}`, background:T.surfaceAlt, color:T.ink, borderRadius:4, padding:"4px 9px", cursor:"pointer", fontWeight:800 }}>✕</button>
            <h3 style={{ margin: "0 0 8px", fontSize: 18, fontFamily: "'Lato',sans-serif" }}>
              📍 Where are you shopping from?
            </h3>
            <p style={{ margin: "0 0 20px", fontSize: 13, color: T.subtext }}>
              Search for your full address, then confirm your ZIP/postal code. The full address is shown in Profile; the postal code powers nearby store search.
            </p>

              <Label>Country</Label>
              <select
                value={manualCountry}
                onChange={e => {
                  setManualCountry(e.target.value);
                  setManualState("");
                  setManualCity("");
                  setManualAddress("");
                  setManualPostalCode("");
                }}
                style={{ width: "100%", background: T.surfaceAlt, border: `1px solid ${T.border}`, borderRadius: 4, padding: "10px 12px", fontSize: 14, marginBottom: 12, boxSizing: "border-box", fontFamily: "'Lato',sans-serif" }}
              >
                <option value="US">United States</option>
                <option value="IN">India</option>
              </select>

              <Label>State / Province</Label>
              <select
                value={manualState}
                onChange={e => {
                  setManualState(e.target.value);
                  setManualCity("");
                  setManualAddress("");
                  setManualPostalCode("");
                }}
                style={{ width: "100%", background: T.surfaceAlt, border: `1px solid ${T.border}`, borderRadius: 4, padding: "10px 12px", fontSize: 14, marginBottom: 12, boxSizing: "border-box", fontFamily: "'Lato',sans-serif" }}
              >
                <option value="">Select a state…</option>
                {(STATES_BY_COUNTRY[manualCountry] || []).map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>

              <Label>City</Label>
              <AutocompleteField
                mode="city"
                country={manualCountry}
                state={manualState}
                value={manualCity}
                onChange={setManualCity}
                onSelect={(desc) => { setManualCity(desc.split(",")[0]); setManualAddress(""); setManualPostalCode(""); }}
                placeholder="Start typing your city…"
                disabled={!manualState}
              />
              <div style={{ marginBottom: 12 }} />

              <Label>Full Address *</Label>
              <AutocompleteField
                mode="address"
                country={manualCountry}
                state={manualState}
                city={manualCity}
                value={manualAddress}
                onChange={setManualAddress}
                onSelect={handleAddressSelect}
                placeholder="Start typing your full address…"
                disabled={!manualCity}
              />
              <p style={{ margin: "8px 0 12px", fontSize: 12, color: T.subtext }}>
                Select your full address from the list when possible. We use the ZIP/postal code from it to find nearby stores.
              </p>

              <Label>{manualCountry === "IN" ? "PIN Code *" : "ZIP / Postal Code *"}</Label>
              <input
                type="text"
                value={manualPostalCode}
                onChange={e => setManualPostalCode(e.target.value)}
                placeholder={manualCountry === "IN" ? "e.g. 500001" : "e.g. 63146"}
                inputMode={manualCountry === "IN" ? "numeric" : "text"}
                autoComplete="postal-code"
                style={{ width: "100%", background: T.surfaceAlt, border: `1px solid ${T.border}`, borderRadius: 4, padding: "10px 12px", fontSize: 14, marginBottom: 12, boxSizing: "border-box", fontFamily: "'Lato',sans-serif" }}
              />

            <button
              onClick={submitManualLocation}
              disabled={!(manualCity.trim() && manualState.trim() && manualPostalCode.trim() && manualAddress.trim())}
              style={{
                width: "100%", padding: "12px", background: T.ink, color: "#FFF",
                border: "none", borderRadius: 4, fontSize: 14, fontWeight: 700,
                textTransform: "uppercase", letterSpacing: "0.04em",
                cursor: !(manualCity.trim() && manualState.trim() && manualPostalCode.trim() && manualAddress.trim()) ? "not-allowed" : "pointer",
                opacity: !(manualCity.trim() && manualState.trim() && manualPostalCode.trim() && manualAddress.trim()) ? 0.5 : 1,
                fontFamily: "'Lato',sans-serif",
                marginTop: 20,
              }}
            >
              Continue
            </button>
          </div>
        </div>
      )}
    </>
  );
}
